import { isEqual } from 'lodash';
import { compressImage } from '../../../../utils/media/compressImage';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslate } from '@tolgee/react';
import {
  Button,
  Dropdown,
  Form,
  InputGroup
} from 'react-bootstrap';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import toast from 'react-hot-toast';
import { PinturaEditor } from '@pqina/react-pintura';
import {
  createDefaultImageReader,
  createDefaultFontFamilyOptions,
  createDefaultImageWriter,
  createMarkupEditorShapeStyleControls,
  getEditorDefaults,
  locale_en_gb,
  plugin_annotate,
  setPlugins,
  type ImageSource,
  type PinturaEditorOptions,
  type Shape
} from '@pqina/pintura';
import {
  LocaleAnnotate,
  LocaleCore,
  LocaleMarkupEditor
} from '@pqina/pintura/locale/en_GB';
import {
  LocaleAnnotate as LocaleAnnotateDe,
  LocaleCore as LocaleCoreDe,
  LocaleMarkupEditor as LocaleMarkupEditorDe
} from '@pqina/pintura/locale/de_DE';
import '@pqina/pintura/pintura.css';
import './LomavisOverlayEditor.css';
import { LomavisConfirmationModal } from '../../../molecules/feedback/LomavisConfirmationModal';
import {
  LomavisFontTypeahead,
  type LomavisFontOption
} from '../../../atoms/inputs/LomavisFontTypeahead';
import { LomavisSketchColorPicker } from '../../../atoms/inputs/LomavisSketchColorPicker';
import { LomavisDropdown1 } from '../../../atoms/overlays/LomavisDropdown1';
import { LomavisOverlayToolTip1 } from '../../../atoms/overlays/lomavisOverlayToolTip1/lomavisOverlayToolTip1';
import { LomavisMoreInfo } from '../../../atoms/tooltips/LomavisMoreInfo';
import { LomavisImageUpload1 } from '../../../molecules/inputs/LomavisImageUpload1';
import { getLanguage } from '../../../../locales/language';
import {
  canvasPresets,
  createLayer,
  hasBackgroundLayer,
  resizeOverlayCanvas,
  serializeOverlay,
  OVERLAY_EDITOR_THUMBNAIL_WIDTH,
  toPinturaShape,
  pinturaShapeToLayerPatch,
  type OverlayDefinition,
  type OverlayLayer,
  type OverlayLayerKind,
  type OverlayPinturaShape
} from './overlayDefinition';

setPlugins(plugin_annotate);

const overlayEditorIcons = {
  alignCenter: 'fa-duotone fa-align-center',
  alignLeft: 'fa-duotone fa-align-left',
  alignRight: 'fa-duotone fa-align-right',
  arrowDownToLine: 'fa-duotone fa-arrow-down-to-line',
  arrowLeft: 'fa-duotone fa-arrow-left',
  backward: 'fa-duotone fa-backward',
  bold: 'fa-duotone fa-bold',
  eye: 'fa-duotone fa-eye',
  eyeSlash: 'fa-duotone fa-eye-slash',
  forward: 'fa-duotone fa-forward',
  italic: 'fa-duotone fa-italic',
  lock: 'fa-duotone fa-lock',
  lockOpen: 'fa-duotone fa-lock-open',
  plus: 'fa-solid fa-plus',
  trash: 'fa-duotone fa-trash'
};

const layerKindIcons: Record<OverlayLayerKind, string> = {
  background: 'fa-duotone fa-palette',
  image: 'fa-duotone fa-image',
  shape: 'fa-duotone fa-shapes',
  text: 'fa-duotone fa-text'
};

/** Language used for the editor interface. Use `en` for English or `de` for German. */
export type OverlayEditorLanguage = 'en' | 'de';

const getCurrentEditorLanguage = (): OverlayEditorLanguage =>
  getLanguage().toLowerCase().startsWith('de') ? 'de' : 'en';

/** A font offered in the text-layer font picker. */
export type OverlayEditorFontOption = LomavisFontOption;

/** Functions supplied to `onExportActionsReady` for exports initiated outside the editor. */
export type OverlayEditorExportActions = {
  /** Sends the serialised template JSON to `onExportTemplate`. */
  exportTemplate: () => void;
  /** Creates a full-size PNG and sends it to `onExportImage`. */
  exportPng: () => Promise<File | undefined>;
  /** Creates a full-size JPEG and sends it to `onExportImage`. */
  exportJpg: () => Promise<File | undefined>;
  /** Creates a PNG thumbnail and sends it to `onExportImage`. */
  exportThumbnail: () => Promise<File | undefined>;
};

/** File type produced by the built-in Save button. Defaults to `json`. */
export type OverlayEditorSaveFormat = 'json' | 'png' | 'jpg' | 'thumbnail';

/** Data emitted after the built-in Save button completes. */
export type OverlayEditorSavePayload =
  | { format: 'json'; template: OverlayDefinition; json: string }
  | { format: 'png' | 'jpg' | 'thumbnail'; file: File; width?: number };

export type OverlayEditorUploadedImage = {
  src: string;
  uuid?: string;
};

const defaultFontOptions: OverlayEditorFontOption[] = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Verdana', label: 'Verdana' }
];

const BUNNY_FONTS_CSS_URL = 'https://fonts.bunny.net/css';

const loadBunnyFontOptions = async (): Promise<OverlayEditorFontOption[]> => {
  const { default: catalogue } = await import(
    '../../../../pages/settings/branding/template_branding/webfonts.json'
  );
  return catalogue.items.map((font) => ({
    value: font.family,
    label: font.family
  }));
};

/**
 * Resolves once a family can actually be drawn with, requesting it if this is
 * the first time it has been asked for.
 *
 * Two waits, and both are needed. The stylesheet's `load` is what registers
 * the `@font-face` rules — until then `document.fonts` has nothing to wait on
 * and would report ready straight away. Once they are registered,
 * `document.fonts.load` is what waits for the file itself, which `display=swap`
 * otherwise leaves arriving after the first paint.
 *
 * Kept per family and shared, so the second caller waits on the first
 * caller's request rather than being told nothing.
 */
const bunnyFontReadiness = new Map<string, Promise<void>>();

/**
 * Whether this family is one the machine already has.
 *
 * Only the built-in four. Bunny does not serve them, so asking fetched a
 * stylesheet for a family that is not there, waited for it, and only then let
 * the first text layer appear.
 *
 * Deliberately not `document.fonts.check`: it answers true for a family with no
 * `@font-face` rule at all, since the browser can draw that name with a
 * fallback and has nothing left to load. Using it here meant every web font
 * looked like it was already available and none was ever fetched — which is
 * every overlay drawn in the wrong face.
 */
const isFontAlreadyUsable = (family: string) =>
  defaultFontOptions.some((font) => font.value === family);

// Returns the shared promise for this family rather than wrapping it in a new
// one, so it is deliberately not `async`.
// eslint-disable-next-line @typescript-eslint/promise-function-async
const requestBunnyFont = (font: OverlayEditorFontOption): Promise<void> => {
  const pending = bunnyFontReadiness.get(font.value);
  if (pending !== undefined) return pending;

  if (isFontAlreadyUsable(font.value)) {
    const ready = Promise.resolve();
    bunnyFontReadiness.set(font.value, ready);
    return ready;
  }

  const stylesheetLoaded = new Promise<void>((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    // Pintura draws text by rasterising an SVG, and an SVG cannot fetch a font
    // itself — so it reads the `@font-face` rules out of `document.styleSheets`
    // and inlines them. A stylesheet loaded without this attribute is opaque to
    // that: touching `cssRules` throws, Pintura finds no rule for the family,
    // and the text is drawn in a fallback face however well the font itself
    // loaded. Bunny serves the CSS with `Access-Control-Allow-Origin`, so
    // asking for it in CORS mode costs nothing and makes the rules readable.
    link.crossOrigin = 'anonymous';
    link.href = `${BUNNY_FONTS_CSS_URL}?family=${encodeURIComponent(
      font.value
    ).replace(/%20/g, '+')}:400,400i,700,700i&display=swap`;
    // Either way the editor has to redraw: a family the browser cannot fetch
    // falls back to one it has, and that text still needs painting.
    link.addEventListener('load', () => resolve());
    link.addEventListener('error', () => {
      bunnyFontReadiness.delete(font.value);
      resolve();
    });
    document.head.append(link);
  });

  const readiness = stylesheetLoaded.then(async () => {
    if (!document.fonts) return;
    try {
      // The shorthand needs a size; the family is the part that matters. Both
      // weights, since a layer may be bold.
      await Promise.all([
        document.fonts.load(`400 16px "${font.value}"`),
        document.fonts.load(`700 16px "${font.value}"`)
      ]);
    } catch {
      // Nothing to do — fall back and redraw regardless.
    }
  });

  bunnyFontReadiness.set(font.value, readiness);
  return readiness;
};

/** Exported for the test that guards which families are fetched. */
export const loadBunnyFont = (
  font: OverlayEditorFontOption,
  onReady?: () => void
): void => {
  if (typeof document === 'undefined') return;

  // Reported every time, including when this family was already asked for.
  // The map outlives any one editor, so an editor opened after the request —
  // which is every editor in the creator, where the fonts are already on the
  // page — used to be told nothing, and left its text unpainted until some
  // interaction happened to redraw it.
  const readiness = requestBunnyFont(font);
  if (onReady) void readiness.then(onReady);
};

/**
 * Families Pintura is told about, on top of the ones it ships with.
 *
 * A face has to be registered before it will be drawn with — loading the
 * stylesheet is not enough on its own, which is why text stayed in a fallback
 * however well the font had loaded for the page.
 */
const getPinturaEditorDefaults = (
  language: OverlayEditorLanguage,
  fontFamilies: string[]
) =>
  getEditorDefaults({
    markupEditorShapeStyleControls: createMarkupEditorShapeStyleControls({
      fontFamilyOptions: [
        ...fontFamilies.map((family) => [family, family] as [string, string]),
        ...createDefaultFontFamilyOptions()
      ]
    }),
    utils: ['annotate'],
    // Pintura seeds its theme by animating a spring from its own `[0, 0, 0]`
    // default towards the colour it reads off the root — `syncColor` does
    // `store.set(value, { hard: !isAnimated })`. Springs advance on
    // `requestAnimationFrame`, which a browser pauses outright in a hidden tab,
    // so an editor created while the tab is in the background leaves that spring
    // frozen at black and never repaints: the artboard reports `load` and draws
    // nothing. `never` makes every such set hard, so the colours land
    // immediately and the first real paint is correct whenever it happens. The
    // artboard has no use for entry animations regardless.
    animations: 'never',
    imageReader: createDefaultImageReader(),
    imageWriter: createDefaultImageWriter(),
    locale:
      language === 'de'
        ? { ...LocaleCoreDe, ...LocaleAnnotateDe, ...LocaleMarkupEditorDe }
        : { ...LocaleCore, ...LocaleAnnotate, ...LocaleMarkupEditor }
  });

export type LomavisOverlayEditorProps = {
  /** Current, application-owned template data. */
  value: OverlayDefinition;
  /** Receives every edit; update the `value` passed back to the editor. */
  onChange: (template: OverlayDefinition) => void;
  /** Receives the template and its serialised JSON when a JSON export occurs. */
  onExportTemplate?: (template: OverlayDefinition, json: string) => void;
  /** Receives an exported PNG, JPG, or thumbnail file without downloading it. */
  onExportImage?: (
    file: File,
    options: { format: 'png' | 'jpg' | 'thumbnail'; width?: number }
  ) => void;
  /** Receives export functions for buttons or actions outside the editor. */
  onExportActionsReady?: (actions: OverlayEditorExportActions) => void;
  /** Chooses the file type made by Save: JSON, PNG, JPG, or a PNG thumbnail. */
  saveFormat?: OverlayEditorSaveFormat;
  /** Receives the completed Save result in the format selected by `saveFormat`. */
  onSave?: (payload: OverlayEditorSavePayload) => void;
  /** Optional label for the built-in Save action. */
  saveLabel?: string;
  /** Disables the Save action while the host is persisting the template. */
  isSaving?: boolean;
  /** Shows upload progress while the host is generating or uploading a saved image. */
  isUploading?: boolean;
  /** Shows a back icon in the header and runs this callback when it is clicked. */
  onBack?: () => void;
  /** Accessible label for the back action. */
  backLabel?: string;
  /** Shows a header icon that immediately downloads the current canvas as a PNG. */
  showDownloadButton?: boolean;
  /** Optional upload handler. Resolve with the persistent image details to use after upload. */
  onUploadImage?: (
    file: File
  ) => Promise<string | OverlayEditorUploadedImage>;
  /** Interface language: `en` for English or `de` for German. Defaults to English. */
  language?: OverlayEditorLanguage;
  /** Extra fonts in the text picker, shown before the built-in fallback fonts. */
  fontOptions?: OverlayEditorFontOption[];
  /** Additional CSS class names for the editor's outer container. */
  className?: string;
  /** Hides the editable overlay name in embedded, per-image editing flows. */
  showTemplateName?: boolean;
  /** Shows Creator's simplified text controls before the remaining layers. */
  quickTextEditing?: boolean;
  /** Server-side validation feedback for the overlay name. */
  templateNameError?: string;
  /** Places the editor header and actions above or below the workspace. */
  headerPosition?: 'top' | 'bottom';
  /** Replaces canvas resizing controls with a read-only size label. */
  readOnlyCanvasSize?: boolean;
  /** Ref attached to the editor header. */
  headerRef?: React.Ref<HTMLElement>;
  /** Additional styles for the header's action group. */
  headerActionsStyle?: React.CSSProperties;
  /** Extra actions rendered alongside the editor's built-in header actions. */
  headerActions?: React.ReactNode;
};

/**
 * Draws the artboard as a white sheet against the grey workspace. Pintura
 * renders this inside its canvas, so it tracks the image exactly — a plain DOM
 * element cannot, as the image is positioned inside the WebGL canvas.
 */
const ARTBOARD_BACKGROUND = [1, 1, 1, 1];

/** Frozen at module scope: a fresh `[]` each render counts as a changed prop. */
const EMPTY_MARKUP_TOOLBAR: never[] = [];

/** Same reasoning, for an artboard that has no layers yet. */
const EMPTY_SHAPES: Shape[] = [];

/** Shared by every "this layer is locked" message, so they replace each other. */

const LOCKED_LAYER_TOAST_ID = 'overlay-editor-locked-layer';

/**
 * Frames to let Pintura render after it reports an artboard loaded, before it
 * is given its shapes. It places and measures the artboard in the first of
 * them; the rest are slack, and cost nothing visible — the editor's loading
 * state is still up.
 */
/** Matches the creator's upload cap, so both paths store comparable images. */
const MAXIMUM_UPLOAD_IMAGE_DIMENSION = 1920;

/**
 * How long the shapes stay held after the pointer goes up when no report
 * follows. A report is a frame or two away, so this never races one; a person
 * reaching for another layer takes far longer, so they never meet a frozen
 * canvas either.
 */
const RELEASE_WITHOUT_REPORT_MS = 250;

const ARTBOARD_SETTLE_FRAMES = 3;

/**
 * How many times an export is asked for before it is reported as failed. The
 * second attempt covers a GPU that was momentarily unable to serve the first.
 */
const EXPORT_ATTEMPTS = 2;

/** Breathing room between export attempts, so the retry is not the same moment. */
const EXPORT_RETRY_DELAY_MS = 150;

const waitBeforeExportRetry = async () =>
  await new Promise((resolve) => {
    // A timer rather than an animation frame: a hidden tab stops painting but
    // still has to be able to finish an export it was asked for.
    window.setTimeout(resolve, EXPORT_RETRY_DELAY_MS);
  });

/**
 * Pintura rejects an aborted export with a bare `{ name: 'AbortError' }`
 * rather than an `Error`, so this cannot lean on `instanceof`.
 */
const isAbortError = (error: unknown) =>
  typeof error === 'object' &&
  error !== null &&
  (error as { name?: string }).name === 'AbortError';

const CANVAS_LOADING_SPINNER_SIZE = '1.75rem';
const CANVAS_UPLOAD_SPINNER_SIZE = '2.5rem';
const MAXIMUM_TEMPLATE_LAYERS = 40;
const SIDEBAR_HEADER_ACTION_SIZE = 40;
const TEMPLATE_IMAGE_ACCEPTED_TYPES = ['image/jpeg', 'image/png'];

type CanvasImageDropStatus = 'ready' | 'occupied' | 'invalid';

const getCanvasImageDropStatus = (
  layer: OverlayLayer | undefined
): CanvasImageDropStatus => {
  if (
    !layer ||
    layer.locked ||
    (layer.kind !== 'background' && layer.kind !== 'image')
  )
    return 'invalid';
  return layer.image?.src ? 'occupied' : 'ready';
};

const getInitialLayerLockState = (layers: OverlayLayer[]) => ({
  lockedLayerIds: new Set(
    layers.filter((layer) => layer.locked).map((layer) => layer.id)
  ),
  textLockedLayerIds: new Set(
    layers
      .filter((layer) => layer.kind === 'text' && layer.textLocked)
      .map((layer) => layer.id)
  )
});

const createBackgroundSource = (
  width: number,
  height: number,
  color: string
): Blob =>
  new Blob(
    [
      color === 'transparent'
        ? `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"/>`
        : `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><rect width="100%" height="100%" fill="${color}"/></svg>`
    ],
    { type: 'image/svg+xml' }
  );

const getImageDimensions = async (
  file: File
): Promise<{ width: number; height: number }> =>
  await new Promise((resolve, reject) => {
    const source = URL.createObjectURL(file);
    const image = new Image();
    const cleanup = () => URL.revokeObjectURL(source);

    image.onload = () => {
      cleanup();
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      cleanup();
      reject(new Error('Unable to determine image dimensions'));
    };
    image.src = source;
  });

const getImageSourceDimensions = async (
  src: string
): Promise<{ width: number; height: number }> =>
  await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () =>
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => reject(new Error('Unable to determine image dimensions'));
    image.src = src;
  });

const getImageLayerBounds = (
  image: { width: number; height: number },
  canvas: OverlayDefinition['canvas'],
  maximumCanvasRatio = 0.8
) => {
  const scale = Math.min(
    (canvas.width * maximumCanvasRatio) / image.width,
    (canvas.height * maximumCanvasRatio) / image.height
  );
  const size = { width: image.width * scale, height: image.height * scale };

  return {
    size,
    position: {
      x: (canvas.width - size.width) / 2,
      y: (canvas.height - size.height) / 2
    }
  };
};

const getCanvasDimension = (value: string): number => {
  const dimension = Number(value);
  return Number.isFinite(dimension) && dimension > 0
    ? Math.max(1, Math.round(dimension))
    : 1;
};

type DropdownOption = {
  value: string;
  label: React.ReactNode;
};

const BootstrapDropdown: React.FC<{
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaLabel: string;
}> = ({ value, options, onChange, disabled = false, ariaLabel }) => {
  return (
    <LomavisDropdown1
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
      ariaLabel={ariaLabel}
    />
  );
};

interface AddLayerToggleProps extends React.ComponentProps<typeof Button> {
  className?: string;
}

const AddLayerToggle = React.forwardRef<HTMLButtonElement, AddLayerToggleProps>(
  ({ className, children, ...props }, ref) => (
    <Button
      {...props}
      ref={ref}
      size="sm"
      variant="outline-secondary"
      className={`d-inline-flex align-items-center justify-content-center rounded-3 p-0 fw-bold text-gray-800 ${
        className?.replace('dropdown-toggle', '') ?? ''
      }`}
      style={{
        width: SIDEBAR_HEADER_ACTION_SIZE,
        height: SIDEBAR_HEADER_ACTION_SIZE
      }}
    >
      {children}
    </Button>
  )
);

AddLayerToggle.displayName = 'AddLayerToggle';

type EmptyTemplatePromptProps = {
  title: string;
  description: string;
  addLayerLabel: string;
  dropdownOpen: boolean;
  setDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  children: React.ReactNode;
};

const EmptyTemplatePrompt: React.FC<EmptyTemplatePromptProps> = ({
  title,
  description,
  addLayerLabel,
  dropdownOpen,
  setDropdownOpen,
  children
}) => (
  <div className="text-center text-dark">
    <div className="mb-2 fs-5 fw-bold">{title}</div>
    <p className="mb-3 text-muted">{description}</p>
    <Dropdown
      show={dropdownOpen}
      onToggle={(nextShow, metadata) => {
        if (metadata.source !== 'click') setDropdownOpen(nextShow);
      }}
    >
      <Dropdown.Toggle
        bsPrefix="lomavis-template-add-layer-toggle"
        size="sm"
        variant="primary"
        className="rounded-3 px-3 fw-semibold shadow-sm"
        aria-label={addLayerLabel}
        onClick={(event) => {
          event.stopPropagation();
          setDropdownOpen((isOpen) => !isOpen);
        }}
      >
        {addLayerLabel}
      </Dropdown.Toggle>
      <Dropdown.Menu className="lomavis-template-action-menu border-0 p-2 shadow">
        {children}
      </Dropdown.Menu>
    </Dropdown>
  </div>
);

type TemplateArtboardProps = {
  editorRef: React.RefObject<PinturaEditor>;
  defaults: ReturnType<typeof getPinturaEditorDefaults>;
  editorKey: string;
  src: Blob;
  annotation: Shape[];
  onReady: () => void;
  onLoadError: (error: unknown) => void;
  onSelectShape: (shape: Shape) => void;
  onUpdateShape: (shape: Shape) => void;
};

/**
 * Isolates Pintura from the host page's render loop.
 *
 * `@pqina/react-pintura` runs `Object.assign(this.editor, this.props)` on every
 * `componentDidUpdate`, and re-assigning the editor's inputs mid-load aborts
 * the image read already in flight. The consuming page re-renders several times
 * while the artboard is still loading — Redux updates, the location query
 * settling, translations arriving — and each of those used to reach the editor
 * and cancel the load, leaving an artboard that never appeared even though
 * Pintura still reported `ready`.
 *
 * Everything passed in therefore has to keep a stable identity across renders,
 * so this only updates when the artboard genuinely changed.
 */
const TemplateArtboard = React.memo(function TemplateArtboard(
  props: TemplateArtboardProps
) {
  const {
    editorRef,
    defaults,
    editorKey,
    src,
    annotation,
    onReady,
    onLoadError,
    onSelectShape,
    onUpdateShape
  } = props;
  // Pintura draws and positions shapes against the artboard, and both need an
  // artboard that is actually on its stage. It has none while it is loading
  // one, and shapes held across that window take the canvas down from inside
  // `pintura.js` — `Cannot read properties of undefined`, on `origin` from its
  // shape layout editor or on `opacity` from its renderer — with no error we
  // can catch. The canvas then stays blank until some later edit happens to
  // push the shapes in again, which is why fitting a layer to the canvas
  // looked like a cure.
  //
  // So the shapes come off while an artboard is loading and go back on once it
  // has settled. Every artboard goes through it, the first included: an editor
  // built with shapes in hand is the same window, which is why an overlay that
  // already had layers could open blank just as a resized one did.
  const [loadedSrc, setLoadedSrc] = useState<Blob>();
  const [artboardSrc, setArtboardSrc] = useState(src);
  const artboardIsUp = loadedSrc === artboardSrc;

  useEffect(() => {
    if (artboardSrc === src) return;
    // Shapes off now, new artboard on the next frame: the editor has to have
    // drawn itself bare before the one it is holding is taken away.
    setLoadedSrc(undefined);
    const frame = requestAnimationFrame(() => setArtboardSrc(src));
    return () => cancelAnimationFrame(frame);
  }, [artboardSrc, src]);

  // While Pintura is dragging a shape it draws the movement itself, from its
  // own copy of the shapes. Handing it a fresh set mid-drag puts ours back on
  // top of the one it is moving, and the drag is over — the shape stands still,
  // or the grab is not taken at all. It does not take a stream of updates for
  // that to happen: pressing a layer selects it, selection travels in these
  // same shapes, and that alone was enough to cancel the drag it had just
  // begun.
  //
  // So hold whatever it already has for the length of the interaction.
  const annotationRef = useRef(annotation);
  annotationRef.current = annotation;
  const [heldAnnotation, setHeldAnnotation] = useState<Shape[] | null>(null);
  const holdAnnotation = useCallback(
    () => setHeldAnnotation(annotationRef.current),
    []
  );

  // Pintura reports the move once, as the pointer goes up. Letting go of the
  // hold at `pointerup` therefore handed back the position the drag started
  // from, which showed as a jump backwards before the report moved it
  // forwards again. The hold outlives the pointer, until what we hold says
  // what Pintura is already showing.
  const releaseWhenCaughtUpRef = useRef(false);
  const releaseFallbackRef = useRef<number>();
  useEffect(() => {
    if (!heldAnnotation) return;
    const release = () => {
      releaseWhenCaughtUpRef.current = true;
      // The hold has to end even when no report follows, because selection
      // travels in these same shapes: one that stays put is a canvas where no
      // other layer can be picked. Long enough that a report — a frame or two
      // away — always wins, short enough to be over before anyone reaches for
      // the next layer.
      window.clearTimeout(releaseFallbackRef.current);
      releaseFallbackRef.current = window.setTimeout(() => {
        if (!releaseWhenCaughtUpRef.current) return;
        releaseWhenCaughtUpRef.current = false;
        setHeldAnnotation(null);
      }, RELEASE_WITHOUT_REPORT_MS);
    };
    // On the window, because a drag routinely ends outside the canvas. In the
    // capture phase, because Pintura handles the pointer itself and a release
    // it stops never reaches a listener waiting on the way back up — and a
    // release we never hear is a canvas frozen for good, selection and all.
    // The hold is taken in the capture phase too; both halves belong in the
    // same one.
    window.addEventListener('pointerup', release, true);
    window.addEventListener('pointercancel', release, true);
    return () => {
      window.removeEventListener('pointerup', release, true);
      window.removeEventListener('pointercancel', release, true);
    };
  }, [heldAnnotation]);

  useEffect(() => {
    if (!heldAnnotation || !releaseWhenCaughtUpRef.current) return;
    window.clearTimeout(releaseFallbackRef.current);
    releaseWhenCaughtUpRef.current = false;
    setHeldAnnotation(null);
  }, [annotation, heldAnnotation]);

  useEffect(() => () => window.clearTimeout(releaseFallbackRef.current), []);

  const artboardSrcRef = useRef(artboardSrc);
  artboardSrcRef.current = artboardSrc;
  const [settledSrc, setSettledSrc] = useState<Blob>();
  const handleLoad = useCallback(
    () => setSettledSrc(artboardSrcRef.current),
    []
  );

  useEffect(() => {
    if (settledSrc !== artboardSrc || artboardIsUp) return;
    // `load` reports the artboard as read, not as placed and measured on the
    // stage, and the shape maths needs the latter. Pintura gets there in its
    // own rendering, so let it render — frames rather than a timer on purpose,
    // since an editor nobody can see renders nothing, and the shapes should
    // keep waiting for exactly as long as that is true.
    let framesLeft = ARTBOARD_SETTLE_FRAMES;
    let frame = 0;
    const settle = () => {
      if (framesLeft === 0) {
        setLoadedSrc(artboardSrc);
        return;
      }
      framesLeft -= 1;
      frame = requestAnimationFrame(settle);
    };
    frame = requestAnimationFrame(settle);

    return () => cancelAnimationFrame(frame);
  }, [artboardIsUp, artboardSrc, settledSrc]);

  // Held back until the shapes are on, so the host's loading state covers the
  // frames in which the artboard is up but still bare.
  useEffect(() => {
    if (artboardIsUp) onReady();
  }, [artboardIsUp, onReady]);

  return (
    <div className="h-100" onPointerDownCapture={holdAnnotation}>
      <PinturaEditor
        {...defaults}
        key={editorKey}
      ref={editorRef}
      className="h-100"
      imageBackgroundColor={ARTBOARD_BACKGROUND}
      src={artboardSrc}
      imageAnnotation={
        artboardIsUp ? heldAnnotation ?? annotation : EMPTY_SHAPES
      }
      onLoad={handleLoad}
      enableToolbar={false}
      enableUtils={false}
      enableZoom={false}
      enableZoomControls={false}
      enableZoomInput={false}
      enableTapToAddText={false}
      enableMoveTool
      enableAutoSelectMoveTool
      markupEditorToolbar={EMPTY_MARKUP_TOOLBAR}
      markupEditorToolRetainStyles={false}
      onLoaderror={onLoadError}
        onSelectshape={onSelectShape}
        onUpdateshape={onUpdateShape}
      />
    </div>
  );
});

export const LomavisOverlayEditor: React.FC<LomavisOverlayEditorProps> = ({
  value,
  onChange,
  onBack,
  backLabel,
  onExportTemplate,
  onExportImage,
  onExportActionsReady,
  saveFormat = 'json',
  onSave,
  saveLabel,
  isSaving = false,
  isUploading = false,
  showDownloadButton = false,
  onUploadImage,
  language = getCurrentEditorLanguage(),
  fontOptions = [],
  className,
  showTemplateName = true,
  quickTextEditing = false,
  templateNameError,
  headerPosition = 'top',
  readOnlyCanvasSize = false,
  headerRef,
  headerActionsStyle,
  headerActions
}) => {
  const { t: translate } = useTranslate();
  const t = useCallback(
    (
      key: string,
      defaultValue: string,
      params?: Record<string, string | number>
    ) => translate({ key, defaultValue, language, params }),
    [language, translate]
  );
  const resolvedBackLabel =
    backLabel ?? t('overlayEditor.actions.back', 'Back');
  const editorRef = useRef<PinturaEditor>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const initializationErrorKeyRef = useRef<string | null>(null);
  const valueRef = useRef(value);
  const previousTemplateIdRef = useRef(value.id);
  const onExportTemplateRef = useRef(onExportTemplate);
  const onExportImageRef = useRef(onExportImage);
  const onSaveRef = useRef(onSave);
  valueRef.current = value;
  onExportTemplateRef.current = onExportTemplate;
  onExportImageRef.current = onExportImage;
  onSaveRef.current = onSave;
  const [tab, setTab] = useState('layers');
  const [canMountArtboard, setCanMountArtboard] = useState(false);
  const [readyPinturaEditorKey, setReadyPinturaEditorKey] = useState<
    string | null
  >(null);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(
    value.layers[0]?.id ?? null
  );
  const [initialLayerLockState, setInitialLayerLockState] = useState(() =>
    getInitialLayerLockState(value.layers)
  );
  const [templateName, setTemplateName] = useState(value.name ?? '');
  const templateNameRef = useRef(templateName);
  templateNameRef.current = templateName;
  /**
   * The last shape handed to Pintura for each layer.
   *
   * Compared by value, not by identity: Pintura writes a drag straight into
   * the object it was given, so by the time the move reaches us through state
   * the object it holds already says the same thing. Recognising that is what
   * keeps the echo from going back.
   */
  const handedShapesRef = useRef(new Map<string, OverlayPinturaShape>());
  const previousShapesRef = useRef<OverlayPinturaShape[] | null>(null);
  /** Set when the canvas is what changed the selection, cleared once answered. */
  const selectionCameFromCanvasRef = useRef(false);

  /**
   * Whether Pintura is already showing what we are about to send it.
   *
   * The rule behind this: nothing that came from Pintura goes back to Pintura.
   * Two things travel inwards — a drag, through `updateshape`, and a selection,
   * through `selectshape` — and each becomes state, which becomes shapes, which
   * would go straight back out. Every return trip is a prop assignment, and the
   * React wrapper answers one by assigning all of them, which Svelte reads as
   * everything having changed. So a round trip is never free, and never useful:
   * it tells Pintura what Pintura told us.
   *
   * A drag is recognised by comparing values, because Pintura writes the move
   * into the object we handed it — so that object already says the new
   * position. A selection is not written there, so it is recognised by where it
   * came from instead.
   */
  const pinturaAlreadyShows = useCallback(
    (handedOver: OverlayPinturaShape, next: OverlayPinturaShape) => {
      if (isEqual(handedOver, next)) return true;
      if (!selectionCameFromCanvasRef.current) return false;
      return isEqual({ ...handedOver, isSelected: next.isSelected }, next);
    },
    []
  );
  const layerShapeCacheRef = useRef<
    Map<
      string,
      {
        layer: OverlayLayer;
        base: OverlayPinturaShape;
      }
    >
  >(new Map());
  const shapeCacheFontRevisionRef = useRef(0);
  const [hasTemplateNameError, setHasTemplateNameError] = useState(false);
  const [selectionRevision, setSelectionRevision] = useState(0);
  const [fontLoadRevision, setFontLoadRevision] = useState(0);
  const [emptyAddLayerDropdownOpen, setEmptyAddLayerDropdownOpen] =
    useState(false);
  const [emptySidebarAddLayerDropdownOpen, setEmptySidebarAddLayerDropdownOpen] =
    useState(false);
  const [sidebarAddLayerDropdownOpen, setSidebarAddLayerDropdownOpen] =
    useState(false);
  const [canvasImageDropActive, setCanvasImageDropActive] = useState(false);
  const [canvasImageDropUploading, setCanvasImageDropUploading] =
    useState(false);
  const [draggedLayerId, setDraggedLayerId] = useState<string | null>(null);
  const [layerDropTarget, setLayerDropTarget] = useState<{
    id: string;
    position: 'before' | 'after';
  } | null>(null);
  const [bunnyFontOptions, setBunnyFontOptions] = useState<
    OverlayEditorFontOption[]
  >([]);
  const [hasLoadedBunnyFontOptions, setHasLoadedBunnyFontOptions] =
    useState(false);
  const selectedLayer = value.layers.find(
    (layer) => layer.id === selectedLayerId
  );
  const canvasImageDropStatus = getCanvasImageDropStatus(selectedLayer);
  const selectedLayerIndex = value.layers.findIndex(
    (layer) => layer.id === selectedLayerId
  );
  const canMoveSelectedLayerBackward =
    selectedLayer?.kind !== 'background' &&
    selectedLayerIndex > 0 &&
    value.layers[selectedLayerIndex - 1].kind !== 'background';
  const canMoveSelectedLayerForward =
    selectedLayer?.kind !== 'background' &&
    selectedLayerIndex >= 0 &&
    selectedLayerIndex < value.layers.length - 1 &&
    value.layers[selectedLayerIndex + 1].kind !== 'background';
  const initiallyLockedLayerIds = initialLayerLockState.lockedLayerIds;
  const initiallyTextLockedLayerIds = initialLayerLockState.textLockedLayerIds;
  const isVisibleInCreator = (layer: OverlayLayer) =>
    layer.kind === 'text'
      ? !(
          initiallyLockedLayerIds.has(layer.id) &&
          initiallyTextLockedLayerIds.has(layer.id)
        )
      : !initiallyLockedLayerIds.has(layer.id);
  const textLayers = quickTextEditing
    ? value.layers.filter(
        (layer) => layer.kind === 'text' && isVisibleInCreator(layer)
      )
    : [];
  const sidebarLayers = quickTextEditing
    ? value.layers.filter(
        (layer) => layer.kind !== 'text' && isVisibleInCreator(layer)
      )
    : value.layers;
  const showCreatorSaveOnly =
    quickTextEditing &&
    value.layers.length > 0 &&
    textLayers.length === 0 &&
    sidebarLayers.length === 0;
  const selectedLayerIsVisible =
    !quickTextEditing || !selectedLayer || isVisibleInCreator(selectedLayer);
  const visibleSelectedLayer = selectedLayerIsVisible ? selectedLayer : null;
  const nameValidationMessage = hasTemplateNameError
    ? t(
        'overlayEditor.overlayName.required',
        'Name is required'
      )
    : templateNameError;
  useEffect(() => {
    setTemplateName(value.name ?? '');
  }, [value.name]);
  useEffect(() => {
    const templateChanged = previousTemplateIdRef.current !== value.id;
    previousTemplateIdRef.current = value.id;
    if (templateChanged) {
      setInitialLayerLockState(getInitialLayerLockState(value.layers));
      setHasTemplateNameError(false);
    }
    setSelectedLayerId((currentId) => {
      if (
        !templateChanged &&
        currentId &&
        value.layers.some((layer) => layer.id === currentId)
      ) {
        return currentId;
      }
      return (
        value.layers.find(
          (layer) =>
            !layer.locked ||
            (quickTextEditing &&
              layer.kind === 'text' &&
              !layer.textLocked)
        )?.id ?? null
      );
    });
    if (templateChanged || value.layers.length === 0) setTab('layers');
  }, [value.id, value.layers]);
  const shapes = useMemo(
    () => {
      if (value.layers.length === 0) return EMPTY_SHAPES;
      if (shapeCacheFontRevisionRef.current !== fontLoadRevision) {
        layerShapeCacheRef.current.clear();
        shapeCacheFontRevisionRef.current = fontLoadRevision;
      }
      const next = value.layers.map((layer) => {
        const cached = layerShapeCacheRef.current.get(layer.id);
        const base =
          cached?.layer === layer ? cached.base : toPinturaShape(layer);
        if (cached?.layer !== layer)
          layerShapeCacheRef.current.set(layer.id, { layer, base });
        const shape = {
          ...base,
          isSelected: layer.id === selectedLayerId && !base.disableSelect
        };

        // Pintura takes a shape it is handed as a new one and lays it out
        // again. After a drag, the shape we would hand back is the one it just
        // told us about — the same values, travelling back where they came
        // from, for it to redo work it has already done. So when nothing has
        // actually changed, it gets back the object it already has.
        const handedOver = handedShapesRef.current.get(layer.id);
        if (handedOver && pinturaAlreadyShows(handedOver, shape))
          return handedOver;
        handedShapesRef.current.set(layer.id, shape);
        return shape;
      });

      // And the array itself. Pintura is a Svelte component behind a React
      // wrapper that assigns every prop on every render, and Svelte counts any
      // object as changed even when it is the identical one — so a new array
      // here, however unchanged its contents, sets the whole editor updating.
      // A profile of a few seconds' editing spent 2.7s inside those updates.
      const previous = previousShapesRef.current;
      if (
        previous &&
        previous.length === next.length &&
        previous.every((shape, index) => shape === next[index])
      )
        return previous;
      previousShapesRef.current = next;
      return next;
    },
    [fontLoadRevision, selectedLayerId, selectionRevision, value.layers]
  );
  // Cleared once the render that answers the click has happened, so a
  // selection made in the sidebar still reaches the canvas.
  useEffect(() => {
    selectionCameFromCanvasRef.current = false;
  }, [selectedLayerId, selectionRevision]);

  // The families this overlay draws with, as one string so the editor options
  // are only rebuilt when the set of them changes — rebuilding them hands
  // Pintura a fresh set of props, which it takes as everything having changed.
  const registeredFontFamilies = useMemo(
    () =>
      [
        ...new Set(
          value.layers
            .filter((layer) => layer.kind === 'text')
            .map((layer) => layer.typography?.fontFamily)
            .filter((family): family is string => Boolean(family))
        )
      ]
        .sort()
        .join('\n'),
    [value.layers]
  );
  const pinturaEditorDefaults = useMemo(
    () =>
      getPinturaEditorDefaults(
        language,
        registeredFontFamilies ? registeredFontFamilies.split('\n') : []
      ),
    [language, registeredFontFamilies]
  );
  // Only the language recreates Pintura, since its locale is fixed at
  // creation. A canvas resize is a new artboard for the editor that is already
  // up: destroying one to resize left Pintura's own redraw — an animation
  // frame it schedules for its textures and never cancels — to fire against a
  // stage whose artboard had gone.
  const pinturaEditorKey = language;
  const artboardSource = useMemo(
    () =>
      createBackgroundSource(
        value.canvas.width,
        value.canvas.height,
        value.canvas.backgroundColor
      ),
    [value.canvas.backgroundColor, value.canvas.height, value.canvas.width]
  );
  const isPinturaReady = readyPinturaEditorKey === pinturaEditorKey;
  const isEditorBusy = isSaving || isUploading;
  const showEmptyTemplatePrompts = isPinturaReady && value.layers.length === 0;

  useEffect(() => {
    const busyRegions = [workspaceRef.current, sidebarRef.current].filter(
      (region): region is HTMLElement => Boolean(region)
    );
    busyRegions.forEach((region) => {
      if (isEditorBusy) region.setAttribute('inert', '');
      else region.removeAttribute('inert');
    });

    return () => busyRegions.forEach((region) => region.removeAttribute('inert'));
  }, [isEditorBusy]);

  // Stable handler identities. `selectLayer`/`updateShape` are redefined on
  // every render, and every changed prop reaches the live editor (see
  // `TemplateArtboard`), so they are routed through refs instead.
  const selectLayerRef = useRef<
    (id: string, openEdit?: boolean, forceFullEdit?: boolean) => void
  >();
  const updateShapeRef = useRef<(shape: Shape) => void>();
  const handleArtboardReady = useCallback(
    () => setReadyPinturaEditorKey(pinturaEditorKey),
    [pinturaEditorKey]
  );
  const handleArtboardLoadError = useCallback((error: unknown) => {
    console.error(
      '[LomavisOverlayEditor] Pintura could not load the local SVG artboard.',
      { error, canvas: valueRef.current.canvas }
    );
  }, []);
  const handleArtboardSelectShape = useCallback((shape: Shape) => {
    const id = (shape as Shape & { id?: string }).id;
    if (!id) return;
    // Pintura selected this itself and is already showing it. Sending the
    // selection back is a prop assignment, and the wrapper assigns every prop
    // at once, which sets the whole Svelte editor updating — the pause between
    // clicking a layer and seeing it selected.
    selectionCameFromCanvasRef.current = true;
    selectLayerRef.current?.(id, true);
  }, []);
  const handleArtboardUpdateShape = useCallback((shape: Shape) => {
    updateShapeRef.current?.(shape);
  }, []);
  const preventPinturaKeyboardShortcuts = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (
        !(event.target instanceof Element) ||
        !event.target.closest('.PinturaRoot')
      )
        return;
      const isPinturaShortcut =
        event.key === 'Backspace' ||
        event.key === 'Delete' ||
        event.key === 'Escape' ||
        event.key === ' ' ||
        event.key.startsWith('Arrow') ||
        ((event.metaKey || event.ctrlKey) &&
          ['d', 'y', 'z'].includes(event.key.toLowerCase()));
      if (!isPinturaShortcut) return;
      event.preventDefault();
      event.stopPropagation();
    },
    []
  );
  // Pintura renders black when it is created against a zero-sized root, and
  // there is no way to detect or repair that afterwards, so hold the editor
  // back until the workspace has a size. The workspace stays rendered and sized
  // throughout: the loading state is painted over it, never in place of it,
  // since replacing it would recreate the very condition this guards against.
  //
  // A measurement is all this needs — no polling and no timeout. Both
  // stylesheets Pintura reads its theme from (`pintura.css` and our override in
  // `LomavisOverlayEditor.css`) are render-blocking `<link>`s in the page's
  // own HTML, so they are already in effect before React renders at all. On a
  // laid-out host the size is there in the first layout pass and the editor
  // mounts in the same commit; a host that is hidden or unsized when it mounts
  // — a tab, a modal, a collapsed section — reports its size through the
  // observer the moment it gets one.
  useLayoutEffect(() => {
    if (canMountArtboard) return;

    const workspace = workspaceRef.current;
    if (!workspace) return;

    const hasSize = () => {
      const { width, height } = workspace.getBoundingClientRect();
      return width > 0 && height > 0;
    };

    if (hasSize()) {
      setCanMountArtboard(true);
      return;
    }

    const observer = new ResizeObserver(() => {
      if (!hasSize()) return;
      observer.disconnect();
      setCanMountArtboard(true);
    });
    observer.observe(workspace);

    return () => observer.disconnect();
  }, [canMountArtboard]);
  // Times how long Pintura takes to report ready, and only once the artboard is
  // actually mounted. A host that is hidden or unsized holds the mount back for
  // as long as it stays that way, and a watchdog started before then would
  // report an initialization failure for an editor that was never created —
  // worse, it would record the key and so swallow the report of a real failure
  // once the host became visible.
  useEffect(() => {
    if (!canMountArtboard || isPinturaReady) return;

    const timeout = window.setTimeout(() => {
      if (initializationErrorKeyRef.current === pinturaEditorKey) return;
      initializationErrorKeyRef.current = pinturaEditorKey;
      const { width = 0, height = 0 } =
        workspaceRef.current?.getBoundingClientRect() ?? {};
      console.error(
        '[LomavisOverlayEditor] Pintura failed to initialize within 8 seconds.',
        {
          canvas: valueRef.current.canvas,
          workspace: { width, height },
        }
      );
    }, 8000);

    return () => window.clearTimeout(timeout);
  }, [canMountArtboard, isPinturaReady, pinturaEditorKey]);
  const availableFontOptions = useMemo(() => {
    const byValue = new Map<string, OverlayEditorFontOption>();
    [...fontOptions, ...bunnyFontOptions, ...defaultFontOptions].forEach(
      (font) => {
        if (!byValue.has(font.value)) byValue.set(font.value, font);
      }
    );
    return [...byValue.values()];
  }, [bunnyFontOptions, fontOptions]);
  /**
   * Families this editor has already redrawn for.
   *
   * A font reports itself ready on every request, so that an editor opened
   * after the request still learns of it. The effect that asks runs whenever
   * the layers change — every pointer move of a drag — so with a text layer on
   * the canvas each move bumped the revision, and a bumped revision throws away
   * every cached shape and rebuilds it, the inline image included. Once per
   * family is all the redraw anyone needs.
   */
  const redrawnForFontRef = useRef(new Set<string>());
  const ensureBunnyFont = useCallback((font: OverlayEditorFontOption) => {
    loadBunnyFont(font, () => {
      if (redrawnForFontRef.current.has(font.value)) return;
      redrawnForFontRef.current.add(font.value);
      setFontLoadRevision((revision) => revision + 1);
    });
  }, []);
  const loadFontCatalogue = useCallback(() => {
    if (hasLoadedBunnyFontOptions) return;
    setHasLoadedBunnyFontOptions(true);
    void loadBunnyFontOptions()
      .then(setBunnyFontOptions)
      .catch(() => setHasLoadedBunnyFontOptions(false));
  }, [hasLoadedBunnyFontOptions]);

  // Fetched as the editor opens rather than when the picker is first focused.
  // It is some 1800 families and arrives as its own file, so asking for it on
  // focus meant opening the picker onto an empty list and waiting. Nothing
  // waits on this — it is a plain import that fills the list once it lands, and
  // by the time anyone reaches for a font it is already there.
  useEffect(() => {
    // Once, as the editor comes up; the callback guards against a second run.
    loadFontCatalogue();
  }, []);
  // The catalogue runs to some 1800 families, and the effect below reruns
  // whenever the layers change — every pointer move of a drag. Building the
  // lookup there rebuilt all of it each time.
  const fontsByFamily = useMemo(
    () => new Map(availableFontOptions.map((font) => [font.value, font])),
    [availableFontOptions]
  );
  useEffect(() => {
    value.layers.forEach((layer) => {
      if (layer.kind === 'text') {
        ensureBunnyFont(
          fontsByFamily.get(layer.typography.fontFamily) ?? {
            value: layer.typography.fontFamily,
            label: layer.typography.fontFamily
          }
        );
      }
    });
  }, [ensureBunnyFont, fontsByFamily, value.layers]);
  const update = (
    updater: (template: OverlayDefinition) => OverlayDefinition
  ) => onChange(updater(valueRef.current));
  const updateLayer = (
    id: string,
    updater: (layer: OverlayLayer) => OverlayLayer
  ) =>
    update((template) => ({
      ...template,
      layers: template.layers.map((layer) =>
        layer.id === id ? updater(layer) : layer
      )
    }));
  const selectLayer = (
    id: string,
    openEdit = false,
    forceFullEdit = false
  ) => {
    const layers = valueRef.current.layers;
    const selectedLayerIndex = layers.findIndex((layer) => layer.id === id);
    const requestedLayer = layers[selectedLayerIndex];
    if (!requestedLayer) {
      // A newly created layer is not in the parent-controlled value until the
      // next render. Keep its intended selection and edit state in the interim.
      setSelectedLayerId(id);
      setSelectionRevision((revision) => revision + 1);
      if (openEdit) setTab('edit');
      return;
    }
    const isCreatorEditableText =
      quickTextEditing &&
      requestedLayer.kind === 'text' &&
      !requestedLayer.textLocked;
    if (requestedLayer.locked && !isCreatorEditableText) {
      // Locked layers are intentionally non-interactive. Keep the current
      // selection rather than making a click appear to select an unrelated
      // layer or clearing a useful selection — but say why nothing happened,
      // since a click that does nothing otherwise reads as the editor being
      // broken. One id, so clicking again replaces the message rather than
      // stacking another copy of it.
      toast(
        t(
          'overlayEditor.layers.lockedNotice',
          'This layer is locked. Unlock it to make changes.'
        ),
        { id: LOCKED_LAYER_TOAST_ID }
      );
      return;
    }

    setSelectedLayerId(requestedLayer.id);
    // Re-apply selection even when this row was already active but Pintura's
    // canvas selection was cleared independently.
    setSelectionRevision((revision) => revision + 1);
    if (
      quickTextEditing &&
      requestedLayer.kind === 'text' &&
      !forceFullEdit
    ) {
      setTab('layers');
      return;
    }
    if (openEdit) setTab('edit');
  };
  const backgroundExists = hasBackgroundLayer(value.layers);
  const hasReachedLayerLimit = value.layers.length >= MAXIMUM_TEMPLATE_LAYERS;
  const addLayer = (
    kind: OverlayLayerKind,
    shapeType?: OverlayLayer['shapeType']
  ) => {
    if (hasReachedLayerLimit) {
      toast(
        t(
          'overlayEditor.layers.maximumReached',
          'You can add up to {{maximum}} layers.',
          { maximum: MAXIMUM_TEMPLATE_LAYERS }
        )
      );
      return;
    }
    if (kind === 'background' && backgroundExists) {
      toast(
        t(
          'overlayEditor.background.alreadyExists',
          'An overlay can only have one background layer.'
        )
      );
      return;
    }
    const layer = createLayer(kind, value.canvas, shapeType);
    update((template) => ({
      ...template,
      // The background enters at the bottom of the stack and stays there.
      layers:
        kind === 'background'
          ? [layer, ...template.layers]
          : [...template.layers, layer]
    }));
    selectLayer(layer.id, kind !== 'text' || !quickTextEditing);
  };
  const updateShape = (shape: Shape) => {
    const id = (shape as Shape & { id?: string }).id;
    if (!id) return;
    const target = value.layers.find((layer) => layer.id === id);
    if (!target) return;
    updateLayer(id, (layer) => ({
      ...layer,
      ...pinturaShapeToLayerPatch(shape, value.canvas, layer)
    }));
  };
  const fitLayerToCanvas = async (id: string) => {
    const initialLayer = valueRef.current.layers.find(
      (layer) => layer.id === id
    );
    if (
      !initialLayer ||
      (initialLayer.kind !== 'background' && initialLayer.kind !== 'image')
    )
      return;

    const preservesAspectRatio =
      initialLayer.kind === 'image'
        ? (initialLayer.image?.lockAspectRatio ?? true)
        : (initialLayer.image?.lockAspectRatio ?? false);
    const imageDimensions =
      preservesAspectRatio && initialLayer.image?.src
        ? await getImageSourceDimensions(initialLayer.image.src).catch(
            () => undefined
          )
        : undefined;

    updateLayer(id, (layer) => {
      const canvas = valueRef.current.canvas;
      const fit = layer.image?.fit ??
        (layer.kind === 'background' ? 'cover' : 'contain');
      const shouldPreserveAspectRatio =
        layer.kind === 'image'
          ? (layer.image?.lockAspectRatio ?? true)
          : (layer.image?.lockAspectRatio ?? false);

      if (!shouldPreserveAspectRatio) {
        return {
          ...layer,
          position: { x: 0, y: 0 },
          size: { width: canvas.width, height: canvas.height }
        };
      }

      const aspectRatio = imageDimensions
        ? imageDimensions.width / imageDimensions.height
        : layer.size.width / layer.size.height;
      const containScale = Math.min(
        canvas.width / aspectRatio,
        canvas.height
      );
      const scale =
        fit === 'cover'
          ? Math.max(canvas.width / aspectRatio, canvas.height)
          : containScale;
      const size = { width: scale * aspectRatio, height: scale };

      return {
        ...layer,
        position: {
          x: (canvas.width - size.width) / 2,
          y: (canvas.height - size.height) / 2
        },
        size
      };
    });
  };
  const removeLayer = (id: string) => {
    const nextLayer = valueRef.current.layers.find((layer) => layer.id !== id);
    update((template) => ({
      ...template,
      layers: template.layers.filter((layer) => layer.id !== id)
    }));
    setSelectedLayerId(nextLayer?.id ?? null);
    setTab('layers');
  };
  const moveLayer = (direction: -1 | 1) => {
    if (!selectedLayerId) return;
    update((template) => {
      const index = template.layers.findIndex(
        (layer) => layer.id === selectedLayerId
      );
      const target = index + direction;
      if (index < 0 || target < 0 || target >= template.layers.length)
        return template;
      // Nothing swaps with the background: it neither rises nor gets buried.
      if (
        template.layers[index].kind === 'background' ||
        template.layers[target].kind === 'background'
      )
        return template;
      const layers = [...template.layers];
      [layers[index], layers[target]] = [layers[target], layers[index]];
      return { ...template, layers };
    });
  };
  const moveLayerTo = (
    sourceId: string,
    targetId: string,
    position: 'before' | 'after'
  ) => {
    if (sourceId === targetId) return;
    update((template) => {
      const sourceIndex = template.layers.findIndex(
        (layer) => layer.id === sourceId
      );
      const targetIndex = template.layers.findIndex(
        (layer) => layer.id === targetId
      );
      if (sourceIndex < 0 || targetIndex < 0) return template;
      if (
        template.layers[sourceIndex].kind === 'background' ||
        template.layers[targetIndex].kind === 'background'
      )
        return template;
      const layers = [...template.layers];
      const [source] = layers.splice(sourceIndex, 1);
      const nextTargetIndex = layers.findIndex(
        (layer) => layer.id === targetId
      );
      layers.splice(
        position === 'after' ? nextTargetIndex + 1 : nextTargetIndex,
        0,
        source
      );
      return { ...template, layers };
    });
  };
  const getLayerDropPosition = (target: HTMLElement, clientY: number) => {
    const bounds = target.getBoundingClientRect();
    return clientY - bounds.top > bounds.height / 2 ? 'after' : 'before';
  };
  const currentTemplate = useCallback(() => {
    const name = templateNameRef.current.trim();
    return showTemplateName && name !== valueRef.current.name
      ? { ...valueRef.current, name }
      : valueRef.current;
  }, [showTemplateName]);
  const exportTemplate = useCallback(() => {
    const template = currentTemplate();
    onExportTemplateRef.current?.(template, serializeOverlay(template));
  }, [currentTemplate]);
  const exportImage = useCallback(
    async (
      mimeType: 'image/png' | 'image/jpeg',
      format: 'png' | 'jpg' | 'thumbnail',
      width?: number
    ) => {
      const processingOptions: PinturaEditorOptions = {
        imageReader: createDefaultImageReader(),
        imageWriter: createDefaultImageWriter({
          mimeType,
          quality: 0.92,
          targetSize: width ? { width, upscale: false } : undefined
        }),
        // JPEG cannot preserve transparency, so its empty pixels use the same
        // white background that users see on the editor artboard.
        imageBackgroundColor:
          mimeType === 'image/jpeg' ? ARTBOARD_BACKGROUND : undefined,
        locale: locale_en_gb
      };

      // Pintura draws the export on the GPU, and that can fail for reasons
      // that have passed by the time it is asked again — a texture it could
      // not allocate this moment, a context still recovering. So ask twice
      // before giving up, which is cheaper for the person exporting than a
      // message telling them to do the same thing by hand.
      for (let attempt = 1; attempt <= EXPORT_ATTEMPTS; attempt += 1) {
        try {
          // Pintura applies writer overrides from the first argument when it is
          // processing the currently loaded image. Passing these as the second
          // argument is only honored while loading a new source.
          const result = await editorRef.current?.editor.processImage(
            processingOptions as unknown as ImageSource
          );
          if (result?.dest) {
            onExportImageRef.current?.(result.dest, { format, width });
            return result.dest;
          }
        } catch (error) {
          // An abort is someone changing their mind, or a second export
          // starting — not a failure, and nothing to say about it.
          if (isAbortError(error)) return undefined;
          if (attempt === EXPORT_ATTEMPTS) {
            console.error('[LomavisOverlayEditor] Export failed.', {
              error,
              format,
              width,
              attempts: attempt
            });
            return undefined;
          }
        }
        if (attempt < EXPORT_ATTEMPTS) await waitBeforeExportRetry();
      }

      // The caller reports this: it knows whether the export was a save, a
      // download, or a preview, and only it can say what was lost.
      return undefined;
    },
    []
  );
  selectLayerRef.current = selectLayer;
  updateShapeRef.current = updateShape;

  const exportActions = useMemo<OverlayEditorExportActions>(
    () => ({
      exportTemplate,
      exportPng: async () => await exportImage('image/png', 'png'),
      exportJpg: async () => await exportImage('image/jpeg', 'jpg'),
      exportThumbnail: async () =>
        await exportImage(
          'image/png',
          'thumbnail',
          OVERLAY_EDITOR_THUMBNAIL_WIDTH
        )
    }),
    [exportImage, exportTemplate]
  );
  useEffect(() => {
    onExportActionsReady?.(exportActions);
  }, [exportActions, onExportActionsReady]);
  const save = useCallback(async () => {
    const name = templateName.trim();
    if (showTemplateName && !name) {
      setHasTemplateNameError(true);
      toast.error(
        t(
          'overlayEditor.overlayName.required',
          'Name is required'
        )
      );
      return;
    }
    if (valueRef.current.layers.length === 0) {
      toast.error(
        t(
          'overlayEditor.layers.required',
          'Add at least one layer before saving.'
        )
      );
      return;
    }
    const template = currentTemplate();
    if (template !== valueRef.current) {
      setTemplateName(name);
      onChange(template);
    }
    try {
      if (saveFormat === 'json') {
        const json = serializeOverlay(template);
        onExportTemplateRef.current?.(template, json);
        onSaveRef.current?.({ format: 'json', template, json });
        return;
      }
      const imageOptions: {
        mimeType: 'image/png' | 'image/jpeg';
        format: 'png' | 'jpg' | 'thumbnail';
        width?: number;
      } =
        saveFormat === 'jpg'
          ? { mimeType: 'image/jpeg' as const, format: 'jpg' as const }
          : saveFormat === 'thumbnail'
          ? {
              mimeType: 'image/png' as const,
              format: 'thumbnail' as const,
              width: OVERLAY_EDITOR_THUMBNAIL_WIDTH
            }
          : { mimeType: 'image/png' as const, format: 'png' as const };
      const file = await exportImage(
        imageOptions.mimeType,
        imageOptions.format,
        imageOptions.width
      );
      if (!file) {
        toast.error(
          t(
            'overlayEditor.errors.export',
            'The overlay could not be exported. Please try again.'
          )
        );
        return;
      }
      onSaveRef.current?.({
        format: imageOptions.format,
        file,
        width: imageOptions.width
      });
    } catch (error) {
      console.error('[LomavisOverlayEditor] Save failed.', error);
      toast.error(
        t('overlayEditor.errors.save', 'Unable to save this overlay.')
      );
    }
  }, [currentTemplate, exportImage, onChange, saveFormat, showTemplateName, t, templateName]);
  const downloadPng = useCallback(async () => {
    const file = await exportImage('image/png', 'png');
    if (!file) {
      toast.error(
        t(
          'overlayEditor.errors.export',
          'The overlay could not be exported. Please try again.'
        )
      );
      return;
    }

    const link = document.createElement('a');
    const filename = currentTemplate().name?.trim() || 'Overlay 1';
    link.href = URL.createObjectURL(file);
    link.download = `${filename.replace(/[\\/:*?"<>|]/g, '-')}.png`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  }, [currentTemplate, exportImage, t]);
  const replaceImage = async (rawFile?: File): Promise<void> => {
    if (!rawFile || !selectedLayer) return;
    // Scaled down the way the creator scales its uploads. An overlay keeps its
    // images inline, so a camera-sized picture would otherwise sit in the
    // definition at full resolution — carried through every save and every
    // edit for detail no canvas this size can show.
    const file = await compressImage(rawFile, {
      maxWidth: MAXIMUM_UPLOAD_IMAGE_DIMENSION,
      maxHeight: MAXIMUM_UPLOAD_IMAGE_DIMENSION
    }).catch(() => rawFile);
    const imageDimensions = await getImageDimensions(file).catch(
      () => undefined
    );
    const setImageSource = ({ src, uuid }: OverlayEditorUploadedImage) =>
      updateLayer(selectedLayer.id, (layer) => ({
        ...layer,
        ...(layer.kind === 'background'
          ? {
              position: { x: 0, y: 0 },
              size: {
                width: valueRef.current.canvas.width,
                height: valueRef.current.canvas.height
              }
            }
          : layer.kind === 'image' && imageDimensions
          ? getImageLayerBounds(imageDimensions, valueRef.current.canvas)
          : {}),
        image: {
          ...layer.image,
          fit:
            layer.image?.fit ??
            (layer.kind === 'background' ? 'cover' : 'contain'),
          src,
          uuid
        }
      }));

    if (onUploadImage) {
      try {
        const uploadedImage = await onUploadImage(file);
        setImageSource(
          typeof uploadedImage === 'string'
            ? { src: uploadedImage }
            : uploadedImage
        );
      } catch {
        toast.error(
          t(
            'overlayEditor.errors.imageUpload',
            'Unable to upload the image. You can keep editing and try again.'
          )
        );
      }
      return;
    }
    await new Promise<void>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSource({ src: String(reader.result) });
        resolve();
      };
      reader.onerror = () => {
        toast.error(
          t(
            'overlayEditor.errors.imageRead',
            'Unable to read the image. You can keep editing and try again.'
          )
        );
        resolve();
      };
      reader.readAsDataURL(file);
    });
  };
  const handleCanvasDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!Array.from(event.dataTransfer.types).includes('Files')) return;
      setCanvasImageDropActive(true);
    },
    []
  );
  const handleCanvasDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!Array.from(event.dataTransfer.types).includes('Files')) return;
      if (event.currentTarget.contains(event.relatedTarget as Node)) return;
      setCanvasImageDropActive(false);
    },
    []
  );
  const handleCanvasDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setCanvasImageDropActive(false);
      if (!Array.from(event.dataTransfer.types).includes('Files')) return;

      if (canvasImageDropStatus === 'invalid') {
        toast.error(
          t(
            'overlayEditor.imageDrop.selectImageLayer',
            'Select an unlocked image or background layer before dropping an image.'
          )
        );
        return;
      }
      if (canvasImageDropStatus === 'occupied') {
        toast.error(
          t(
            'overlayEditor.imageDrop.imageAlreadySet',
            'This layer already contains an image. Remove it before dropping a new one.'
          )
        );
        return;
      }

      const files = Array.from(event.dataTransfer.files);
      if (
        files.length !== 1 ||
        !TEMPLATE_IMAGE_ACCEPTED_TYPES.includes(files[0].type)
      ) {
        toast.error(
          t(
            'overlayEditor.imageDrop.unsupportedFile',
            'Drop one PNG or JPG image to replace this layer.'
          )
        );
        return;
      }

      setCanvasImageDropUploading(true);
      void replaceImage(files[0]).finally(() =>
        setCanvasImageDropUploading(false)
      );
    },
    [canvasImageDropStatus, replaceImage, t]
  );
  const setCanvas = (presetId: string) => {
    if (presetId === 'custom')
      return update((template) => ({
        ...template,
        canvas: { ...template.canvas, preset: 'custom' }
      }));
    const preset = canvasPresets.find((item) => item.id === presetId);
    if (preset)
      update((template) =>
        resizeOverlayCanvas(template, preset.width, preset.height, preset.id)
      );
  };
  const addLayerOptions: Array<{
    kind: OverlayLayerKind;
    title: string;
    subtitle: string;
  }> = [
    {
      kind: 'background',
      title: t('overlayEditor.actions.addBackgroundLayer', 'Background layer'),
      subtitle: t(
        'overlayEditor.actions.addBackgroundLayerDescription',
        'Set the canvas color or background image.'
      )
    },
    {
      kind: 'text',
      title: t('overlayEditor.actions.addTextLayer', 'Text layer'),
      subtitle: t(
        'overlayEditor.actions.addTextLayerDescription',
        'Add editable text to your design.'
      )
    },
    {
      kind: 'image',
      title: t('overlayEditor.actions.addImageLayer', 'Image layer'),
      subtitle: t(
        'overlayEditor.actions.addImageLayerDescription',
        'Upload and position an image.'
      )
    },
    {
      kind: 'shape',
      title: t('overlayEditor.actions.addShapeLayer', 'Shape layer'),
      subtitle: t(
        'overlayEditor.actions.addShapeLayerDescription',
        'Add a rectangle, circle, or line.'
      )
    }
  ];
  const renderAddLayerOptions = (closeDropdown: () => void) =>
    addLayerOptions
      .filter(({ kind }) => kind !== 'background' || !backgroundExists)
      .map(({ kind, title, subtitle }) => (
        <Dropdown.Item
          key={kind}
          onClick={() => {
            addLayer(kind);
            closeDropdown();
          }}
        >
          <span className="lomavis-template-add-layer-title">{title}</span>
          <span className="lomavis-template-add-layer-subtitle">
            {subtitle}
          </span>
        </Dropdown.Item>
      ));

  const saveButton = (
    <Button
      type="button"
      size="sm"
      variant={showCreatorSaveOnly ? 'secondary' : 'primary'}
      className="rounded-3 px-4 fw-semibold shadow-sm"
      disabled={isEditorBusy || !isPinturaReady}
      onClick={() => {
        void save();
      }}
    >
      {isEditorBusy && (
        <span
          className="spinner-border spinner-border-sm me-2"
          aria-hidden="true"
        />
      )}
      {isUploading
        ? t('overlayEditor.actions.uploading', 'Uploading…')
        : isSaving
        ? t('overlayEditor.actions.saving', 'Saving…')
        : saveLabel || t('overlayEditor.actions.save', 'Save')}
    </Button>
  );

  const editorHeader = (
    <header
      ref={headerRef}
      className={`d-flex flex-wrap align-items-center gap-2 p-3 ${
        headerPosition === 'bottom' ? 'border-top' : 'border-bottom'
      }`}
    >
        {onBack && (
          <LomavisOverlayToolTip1
            placement="bottom"
            creatorStyle
            text={resolvedBackLabel}
          >
            <Button
              type="button"
              size="sm"
              variant="light"
              className="lomavis-template-layer-action bg-white text-gray-700"
              aria-label={resolvedBackLabel}
              disabled={isEditorBusy}
              onClick={onBack}
            >
              <i
                className={overlayEditorIcons.arrowLeft}
                aria-hidden="true"
              />
            </Button>
          </LomavisOverlayToolTip1>
        )}
        {showTemplateName && (
          <Form.Group style={{ width: 345, maxWidth: '100%' }}>
            <Form.Control
              aria-label={t('overlayEditor.overlayName', 'Overlay name')}
              aria-describedby={
                nameValidationMessage
                  ? 'template-editor-name-feedback'
                  : undefined
              }
              aria-invalid={Boolean(nameValidationMessage)}
              className="fw-semibold"
              isInvalid={Boolean(nameValidationMessage)}
              disabled={isEditorBusy}
              title={nameValidationMessage}
              value={templateName}
              placeholder={t(
                'overlayEditor.overlayName.placeholder',
                'Name'
              )}
              onChange={(event) => {
                const name = event.target.value;
                if (name.trim()) setHasTemplateNameError(false);
                setTemplateName(name);
              }}
            />
            {nameValidationMessage && (
              <Form.Control.Feedback
                id="template-editor-name-feedback"
                type="invalid"
                className="visually-hidden"
              >
                {nameValidationMessage}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        )}
      {readOnlyCanvasSize ? (
          <div
            aria-label={t('overlayEditor.canvasSize', 'Canvas size')}
            className="px-2 text-muted small"
          >
            {value.canvas.width} × {value.canvas.height}
          </div>
        ) : (
          <Form.Group style={{ width: 250, maxWidth: '100%' }}>
            <BootstrapDropdown
              value={value.canvas.preset}
              ariaLabel={t('overlayEditor.canvasSize', 'Canvas size')}
              onChange={setCanvas}
              disabled={isEditorBusy}
              options={[
                ...canvasPresets.map((preset) => ({
                  value: preset.id,
                  label: `${t(
                    `overlayEditor.presets.${preset.id}`,
                    preset.label
                  )} · ${preset.width} × ${preset.height}`
                })),
                {
                  value: 'custom',
                  label: t('overlayEditor.presets.custom', 'Custom…')
                }
              ]}
            />
            {value.canvas.preset === 'custom' && (
              <InputGroup className="mt-2">
                <Form.Control
                  type="number"
                  min="1"
                  value={value.canvas.width}
                  aria-label={t('overlayEditor.canvasWidth', 'Canvas width')}
                  disabled={isEditorBusy}
                  onChange={(event) =>
                    update((template) =>
                      resizeOverlayCanvas(
                        template,
                        getCanvasDimension(event.target.value),
                        template.canvas.height,
                        'custom'
                      )
                    )
                  }
                />
                <InputGroup.Text>×</InputGroup.Text>
                <Form.Control
                  type="number"
                  min="1"
                  value={value.canvas.height}
                  aria-label={t('overlayEditor.canvasHeight', 'Canvas height')}
                  disabled={isEditorBusy}
                  onChange={(event) =>
                    update((template) =>
                      resizeOverlayCanvas(
                        template,
                        template.canvas.width,
                        getCanvasDimension(event.target.value),
                        'custom'
                      )
                    )
                  }
                />
              </InputGroup>
            )}
          </Form.Group>
        )}
        {isPinturaReady && (
          <div
            className="ms-auto d-flex align-items-center gap-2 pe-2"
            style={headerActionsStyle}
          >
            {headerActions}
            {showDownloadButton && (
              <LomavisOverlayToolTip1
                placement="bottom"
                creatorStyle
                text={t(
                  'overlayEditor.actions.downloadPng',
                  'Download PNG'
                )}
              >
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="lomavis-template-layer-action bg-white text-gray-700"
                  aria-label={t(
                    'overlayEditor.actions.downloadPng',
                    'Download PNG'
                  )}
                  disabled={isEditorBusy}
                  onClick={() => {
                    void downloadPng();
                  }}
                >
                  <i
                    className={overlayEditorIcons.arrowDownToLine}
                    aria-hidden="true"
                  />
                </Button>
              </LomavisOverlayToolTip1>
            )}
            {!showCreatorSaveOnly && saveButton}
          </div>
        )}
    </header>
  );

  return (
    <div
      className={`lomavis-template-editor d-flex w-100 flex-column overflow-hidden border rounded-3 bg-white ${className ?? ''}`}
      style={{
        color: 'var(--bs-gray-900, #071437)',
        fontFamily: 'Inter, Helvetica, sans-serif'
      }}
    >
      {headerPosition === 'top' && editorHeader}
      <main
        className="d-grid flex-grow-1 overflow-hidden"
        aria-busy={isEditorBusy}
        style={{
          gridTemplateColumns: '360px minmax(0, 1fr)',
          minHeight: 0,
          flexBasis: 0
        }}
      >
        <section
          className="lomavis-template-workspace position-relative d-flex flex-column overflow-hidden"
          style={{ minWidth: 0, minHeight: 0 }}
        >
          <div
            ref={workspaceRef}
            className="position-relative flex-grow-1"
            style={{ minHeight: 0, touchAction: 'none' }}
            onKeyDownCapture={preventPinturaKeyboardShortcuts}
            onDragOverCapture={handleCanvasDragOver}
            onDragLeaveCapture={handleCanvasDragLeave}
            onDropCapture={handleCanvasDrop}
          >
            <div className="position-relative h-100">
              {canMountArtboard && (
                <TemplateArtboard
                  editorRef={editorRef}
                  defaults={pinturaEditorDefaults}
                  editorKey={pinturaEditorKey}
                  src={artboardSource}
                  annotation={shapes}
                  onReady={handleArtboardReady}
                  onLoadError={handleArtboardLoadError}
                  onSelectShape={handleArtboardSelectShape}
                  onUpdateShape={handleArtboardUpdateShape}
                />
              )}
            </div>
            {!isPinturaReady && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{
                  zIndex: 3,
                  background:
                    'rgb(var(--lomavis-template-workspace-bg, 245, 245, 247))'
                }}
                role="status"
                aria-live="polite"
              >
                <div className="d-flex flex-column align-items-center gap-3 rounded bg-white p-4 shadow-sm text-center">
                  <span
                    className="spinner-border text-primary"
                    style={{
                      width: CANVAS_LOADING_SPINNER_SIZE,
                      height: CANVAS_LOADING_SPINNER_SIZE
                    }}
                    aria-hidden="true"
                  />
                  <span className="fw-semibold text-gray-700">
                    {t('overlayEditor.loading', 'Preparing the editor…')}
                  </span>
                </div>
              </div>
            )}
            {canvasImageDropUploading && (
              <div
                className="position-absolute top-0 start-0 z-3 d-flex h-100 w-100 align-items-center justify-content-center bg-light bg-opacity-75"
                role="status"
                aria-live="polite"
              >
                <div className="d-flex align-items-center gap-2 rounded bg-white px-3 py-2 shadow-sm text-primary">
                  <span
                    className="spinner-border"
                    style={{
                      width: CANVAS_UPLOAD_SPINNER_SIZE,
                      height: CANVAS_UPLOAD_SPINNER_SIZE
                    }}
                    aria-hidden="true"
                  />
                  {t('overlayEditor.actions.uploading', 'Uploading…')}
                </div>
              </div>
            )}
            {canvasImageDropActive && (
              <div className="position-absolute top-0 start-0 z-2 d-flex h-100 w-100 align-items-center justify-content-center bg-primary bg-opacity-10 pe-none">
                <span className="rounded bg-white px-3 py-2 text-primary shadow-sm">
                  {canvasImageDropStatus === 'ready'
                    ? t(
                        'overlayEditor.imageDrop.add',
                        'Drop to add an image to this layer.'
                      )
                    : canvasImageDropStatus === 'occupied'
                    ? t(
                        'overlayEditor.imageDrop.imageAlreadySet',
                        'This layer already contains an image. Remove it before dropping a new one.'
                      )
                    : t(
                        'overlayEditor.imageDrop.selectImageLayer',
                        'Select an unlocked image or background layer before dropping an image.'
                      )}
                </span>
              </div>
            )}
            {showEmptyTemplatePrompts && (
              <div
                className="position-absolute top-50 start-50 translate-middle"
                style={{ width: 280, zIndex: 2 }}
              >
                <EmptyTemplatePrompt
                  title={t('overlayEditor.empty.title', 'Start your overlay')}
                  description={t(
                    'overlayEditor.empty.description',
                    'Add a layer to start building your design.'
                  )}
                  addLayerLabel={t('overlayEditor.actions.add', 'Add layer')}
                  dropdownOpen={emptyAddLayerDropdownOpen}
                  setDropdownOpen={setEmptyAddLayerDropdownOpen}
                >
                  {renderAddLayerOptions(() =>
                    setEmptyAddLayerDropdownOpen(false)
                  )}
                </EmptyTemplatePrompt>
              </div>
            )}
          </div>
          {isEditorBusy && (
            <div
              className="position-absolute top-0 start-0 z-3 d-flex h-100 w-100 align-items-center justify-content-center bg-light bg-opacity-50 pe-auto"
              role="status"
              aria-live="polite"
            >
              <span
                className="spinner-border text-primary"
                style={{
                  width: CANVAS_UPLOAD_SPINNER_SIZE,
                  height: CANVAS_UPLOAD_SPINNER_SIZE
                }}
                aria-hidden="true"
              />
              <span className="visually-hidden">
                {t('overlayEditor.actions.uploading', 'Uploading…')}
              </span>
            </div>
          )}
        </section>
        <aside
          ref={sidebarRef}
          className={`order-first d-flex h-100 flex-column overflow-hidden border-end bg-white ${
            isEditorBusy ? 'opacity-75' : ''
          }`}
          aria-busy={isEditorBusy}
          style={{ minHeight: 0 }}
        >
          {!showCreatorSaveOnly && (
          <div className="d-flex flex-shrink-0 align-items-center gap-3 border-bottom bg-white px-3 py-3">
            <div
              role="tablist"
              aria-label={t(
                'overlayEditor.sidebarLabel',
                'Overlay editor sidebar'
              )}
              className="d-flex flex-grow-1 rounded-3 bg-light p-1"
            >
              <Button
                size="sm"
                variant="light"
                role="tab"
                aria-selected={tab === 'layers'}
                className={`lomavis-template-sidebar-tab flex-grow-1 rounded-3 border-0 py-2 fs-6 fw-medium ${
                  tab === 'layers'
                    ? 'is-active bg-white text-dark shadow-sm'
                    : 'bg-transparent text-muted shadow-none'
                }`}
                onClick={() => setTab('layers')}
              >
                {t('overlayEditor.tabs.layers', 'Layers')}
              </Button>
              <Button
                size="sm"
                variant="light"
                role="tab"
                aria-selected={tab === 'edit'}
                disabled={!visibleSelectedLayer}
                className={`lomavis-template-sidebar-tab flex-grow-1 rounded-3 border-0 py-2 fs-6 fw-medium ${
                  tab === 'edit'
                    ? 'is-active bg-white text-dark shadow-sm'
                    : 'bg-transparent text-muted shadow-none'
                }`}
                onClick={() => setTab('edit')}
              >
                {t('overlayEditor.tabs.editLayer', 'Edit layer')}
              </Button>
            </div>
            <Dropdown
              align="end"
              show={sidebarAddLayerDropdownOpen}
              onToggle={(nextShow, metadata) => {
                if (metadata.source !== 'click')
                  setSidebarAddLayerDropdownOpen(nextShow);
              }}
            >
              <Dropdown.Toggle
                as={AddLayerToggle}
                aria-label={t('overlayEditor.actions.add', 'Add layer')}
                onClick={(event) => {
                  event.stopPropagation();
                  setSidebarAddLayerDropdownOpen((isOpen) => !isOpen);
                }}
              >
                <i
                  className={overlayEditorIcons.plus}
                  aria-hidden="true"
                />
              </Dropdown.Toggle>
              <Dropdown.Menu
                align="end"
                flip={false}
                className="lomavis-template-action-menu border-0 p-2 shadow"
              >
                {renderAddLayerOptions(() =>
                  setSidebarAddLayerDropdownOpen(false)
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
          )}
          {showCreatorSaveOnly ? (
            <div className="d-flex flex-grow-1 flex-column align-items-center justify-content-center gap-3 p-3 text-center">
              <span className="text-muted">
                {t(
                  'overlayEditor.layers.noEditable',
                  'No editable layers are in this overlay.'
                )}
              </span>
              {saveButton}
            </div>
          ) : (
          <>
          {tab === 'layers' && showEmptyTemplatePrompts ? (
            <div className="d-flex flex-grow-1 align-items-center justify-content-center p-3 text-center">
              <EmptyTemplatePrompt
                title={t('overlayEditor.empty.title', 'Start your overlay')}
                description={t(
                  'overlayEditor.empty.description',
                  'Add a layer to start building your design.'
                )}
                addLayerLabel={t('overlayEditor.actions.add', 'Add layer')}
                dropdownOpen={emptySidebarAddLayerDropdownOpen}
                setDropdownOpen={setEmptySidebarAddLayerDropdownOpen}
              >
                {renderAddLayerOptions(() =>
                  setEmptySidebarAddLayerDropdownOpen(false)
                )}
              </EmptyTemplatePrompt>
            </div>
          ) : (
            <SimpleBar
              forceVisible="y"
              autoHide={false}
              className={`lomavis-template-sidebar-scroll flex-grow-1 ${
                quickTextEditing
                  ? 'lomavis-template-creator-sidebar-scroll'
                  : ''
              }`}
              style={{ minHeight: 0, height: '100%', flexBasis: 0 }}
            >
              {tab === 'layers' ? (
                <div
                  className={
                    quickTextEditing
                      ? 'lomavis-template-creator-layer-groups'
                      : undefined
                  }
                >
                  {textLayers.length > 0 && (
                    <QuickTextLayerFields
                      layers={textLayers}
                      language={language}
                      onUpdate={(id, updater) => updateLayer(id, updater)}
                      onFocusLayer={(id) => selectLayer(id)}
                      onMoreOptions={(id) => selectLayer(id, true, true)}
                    />
                  )}
                  <div
                    className={`lomavis-template-layer-list p-2 ${
                      quickTextEditing
                        ? 'lomavis-template-creator-layer-list'
                        : ''
                    }`}
                  >
                  {sidebarLayers.map((layer) => (
                    <div
                      role="button"
                      tabIndex={0}
                      key={layer.id}
                      draggable={layer.kind !== 'background'}
                      title={
                        layer.kind !== 'background'
                          ? t(
                              'overlayEditor.actions.dragToReorder',
                              'Drag to reorder layer'
                            )
                          : undefined
                      }
                      className={`lomavis-template-layer-list-item d-grid w-100 align-items-center gap-2 p-2 text-start ${
                        selectedLayerId === layer.id
                          ? 'is-selected'
                          : ''
                      } ${
                        draggedLayerId === layer.id ? 'is-dragging' : ''
                      } ${
                        layerDropTarget?.id === layer.id
                          ? `is-drop-${layerDropTarget.position}`
                          : ''
                      }`}
                      onDragStart={(event) => {
                        if (
                          layer.kind === 'background' ||
                          (event.target instanceof Element &&
                            event.target.closest('button'))
                        ) {
                          event.preventDefault();
                          return;
                        }
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', layer.id);
                        setDraggedLayerId(layer.id);
                      }}
                      onDragOver={(event) => {
                        if (
                          !draggedLayerId ||
                          draggedLayerId === layer.id ||
                          layer.kind === 'background'
                        )
                          return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'move';
                        const position = getLayerDropPosition(
                          event.currentTarget,
                          event.clientY
                        );
                        setLayerDropTarget((current) =>
                          current?.id === layer.id &&
                          current.position === position
                            ? current
                            : { id: layer.id, position }
                        );
                      }}
                      onDragLeave={(event) => {
                        if (
                          event.relatedTarget instanceof Node &&
                          event.currentTarget.contains(event.relatedTarget)
                        )
                          return;
                        setLayerDropTarget((current) =>
                          current?.id === layer.id ? null : current
                        );
                      }}
                      onDragEnd={() => {
                        setDraggedLayerId(null);
                        setLayerDropTarget(null);
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        const sourceId =
                          draggedLayerId ||
                          event.dataTransfer.getData('text/plain');
                        if (
                          sourceId &&
                          sourceId !== layer.id &&
                          layer.kind !== 'background'
                        ) {
                          const position =
                            layerDropTarget?.id === layer.id
                              ? layerDropTarget.position
                              : getLayerDropPosition(
                                  event.currentTarget,
                                  event.clientY
                                );
                          moveLayerTo(sourceId, layer.id, position);
                        }
                        setDraggedLayerId(null);
                        setLayerDropTarget(null);
                      }}
                      onClick={() => selectLayer(layer.id, true)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          selectLayer(layer.id, true);
                        }
                      }}
                      >
                      <span
                        className={`lomavis-template-layer-kind lomavis-template-layer-kind-${layer.kind}`}
                      >
                        <i
                          className={layerKindIcons[layer.kind]}
                          aria-hidden="true"
                        />
                      </span>
                      <span className="text-truncate fw-semibold">
                        {layer.name}
                      </span>
                      <Button
                        type="button"
                        variant="link"
                        className="lomavis-template-layer-list-action"
                        title={t(
                          'overlayEditor.actions.toggleVisibility',
                          'Toggle layer visibility'
                        )}
                        aria-label={t(
                          'overlayEditor.actions.toggleVisibility',
                          'Toggle layer visibility'
                        )}
                        onClick={(event) => {
                          event.stopPropagation();
                          updateLayer(layer.id, (current) => ({
                            ...current,
                            visible: !current.visible
                          }));
                        }}
                      >
                        <i
                          className={
                            layer.visible
                              ? overlayEditorIcons.eye
                              : overlayEditorIcons.eyeSlash
                          }
                          aria-hidden="true"
                        />
                      </Button>
                      <Button
                        type="button"
                        variant="link"
                        className="lomavis-template-layer-list-action"
                        title={t(
                          'overlayEditor.actions.toggleLock',
                          'Toggle layer lock'
                        )}
                        aria-label={t(
                          'overlayEditor.actions.toggleLock',
                          'Toggle layer lock'
                        )}
                        onClick={(event) => {
                          event.stopPropagation();
                          updateLayer(layer.id, (current) => {
                            const locked = !current.locked;
                            return {
                              ...current,
                              locked,
                              ...(current.kind === 'text' && !locked
                                ? { textLocked: false }
                                : {})
                            };
                          });
                        }}
                      >
                        <i
                          className={
                            layer.locked
                              ? overlayEditorIcons.lock
                              : overlayEditorIcons.lockOpen
                          }
                          aria-hidden="true"
                        />
                      </Button>
                    </div>
                  ))}
                  </div>
                </div>
              ) : visibleSelectedLayer ? (
                <LayerFields
                  layer={visibleSelectedLayer}
                  language={language}
                  fontOptions={availableFontOptions}
                  showTextLock={!quickTextEditing}
                  allowTextDetailsWhenLayerLocked={quickTextEditing}
                  canMoveBackward={canMoveSelectedLayerBackward}
                  canMoveForward={canMoveSelectedLayerForward}
                  onFontSelect={ensureBunnyFont}
                  onFontPickerFocus={loadFontCatalogue}
                  onUpdate={(updater) =>
                    updateLayer(visibleSelectedLayer.id, updater)
                  }
                  onMove={moveLayer}
                  onDelete={() => removeLayer(visibleSelectedLayer.id)}
                  onFitToCanvas={() => {
                    void fitLayerToCanvas(visibleSelectedLayer.id);
                  }}
                  onImageSelect={async (file) => {
                    await replaceImage(file);
                  }}
                  onImageRemove={() =>
                    updateLayer(visibleSelectedLayer.id, (layer) => ({
                      ...layer,
                      image: {
                        ...layer.image,
                        fit: layer.kind === 'background' ? 'cover' : 'contain',
                        src: undefined,
                        uuid: undefined
                      }
                    }))
                  }
                />
              ) : null}
            </SimpleBar>
          )}
          {tab === 'edit' && visibleSelectedLayer && (
            <div className="flex-shrink-0 border-top bg-white p-3">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="d-flex w-100 align-items-center justify-content-center gap-2"
                onClick={() => setTab('layers')}
              >
                <i
                  className={`fs-3 ${overlayEditorIcons.arrowLeft}`}
                  aria-hidden="true"
                />
                {t('overlayEditor.actions.backToLayers', 'Back to layers')}
              </Button>
            </div>
          )}
          </>
          )}
        </aside>
      </main>
      {headerPosition === 'bottom' && editorHeader}
    </div>
  );
};

const MINIMUM_TEXT_FONT_SIZE = 8;

const getCanvasFont = (
  typography: NonNullable<OverlayLayer['typography']>,
  fontSize: number
) =>
  `${typography.fontStyle} ${typography.fontWeight} ${fontSize}px "${typography.fontFamily.replace(/"/g, '\\"')}"`;

const measureTextWidth = (
  context: CanvasRenderingContext2D,
  text: string,
  letterSpacing: number
) =>
  context.measureText(text).width +
  Math.max(0, text.length - 1) * letterSpacing;

const getWrappedTextLines = (
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  letterSpacing: number
) =>
  text.split('\n').flatMap((paragraph) => {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (words.length === 0) return [''];

    const lines: string[] = [];
    let line = '';
    words.forEach((word) => {
      const candidate = line ? `${line} ${word}` : word;
      if (
        line &&
        measureTextWidth(context, candidate, letterSpacing) > maxWidth
      ) {
        lines.push(line);
        line = word;
        return;
      }
      line = candidate;
    });
    lines.push(line);
    return lines;
  });

const textFitsLayer = (
  context: CanvasRenderingContext2D,
  layer: OverlayLayer,
  text: string,
  fontSize: number
) => {
  if (!layer.typography) return true;
  const { typography } = layer;
  context.font = getCanvasFont(typography, fontSize);
  const lines = getWrappedTextLines(
    context,
    text,
    layer.size.width,
    typography.letterSpacing
  );
  const widestLine = Math.max(
    ...lines.map((line) =>
      measureTextWidth(context, line, typography.letterSpacing)
    )
  );
  return (
    widestLine <= layer.size.width &&
    lines.length * fontSize * typography.lineHeight <= layer.size.height
  );
};

const updateTextToFitLayer = (
  layer: OverlayLayer,
  text: string,
  maximumFontSize = layer.typography?.fontSize
) => {
  if (
    layer.kind !== 'text' ||
    !layer.typography ||
    !maximumFontSize ||
    typeof document === 'undefined'
  )
    return { ...layer, text };

  const context = document.createElement('canvas').getContext('2d');
  if (!context) return { ...layer, text };

  const fontSize = Math.max(MINIMUM_TEXT_FONT_SIZE, maximumFontSize);
  if (textFitsLayer(context, layer, text, fontSize))
    return {
      ...layer,
      text,
      typography: { ...layer.typography, fontSize }
    };

  let lowerBound = MINIMUM_TEXT_FONT_SIZE;
  let upperBound = Math.floor(fontSize);
  let fittingFontSize = MINIMUM_TEXT_FONT_SIZE;
  while (lowerBound <= upperBound) {
    const candidate = Math.floor((lowerBound + upperBound) / 2);
    if (textFitsLayer(context, layer, text, candidate)) {
      fittingFontSize = candidate;
      lowerBound = candidate + 1;
    } else {
      upperBound = candidate - 1;
    }
  }

  return {
    ...layer,
    text,
    typography: { ...layer.typography, fontSize: fittingFontSize }
  };
};

const expandTextLayerToFit = (layer: OverlayLayer, text: string) => {
  if (layer.kind !== 'text' || !layer.typography)
    return { ...layer, text };

  const { typography } = layer;
  const lines = text.split('\n');
  const minimumHeight = Math.ceil(
    lines.length * typography.fontSize * typography.lineHeight +
      typography.fontSize * 0.35
  );
  return {
    ...layer,
    text,
    size: {
      ...layer.size,
      height: Math.max(layer.size.height, minimumHeight)
    }
  };
};

const QuickTextLayerField: React.FC<{
  layer: OverlayLayer;
  moreOptionsLabel: string;
  onUpdate: (id: string, updater: (layer: OverlayLayer) => OverlayLayer) => void;
  onFocusLayer: (id: string) => void;
  onMoreOptions: (id: string) => void;
}> = ({ layer, moreOptionsLabel, onUpdate, onFocusLayer, onMoreOptions }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const onUpdateRef = useRef(onUpdate);
  const maximumFontSizeRef = useRef(layer.typography?.fontSize);
  onUpdateRef.current = onUpdate;

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [layer.text]);

  return (
    <Form.Group>
      <Form.Label className="mb-1 small fw-semibold">{layer.name}</Form.Label>
      <Form.Control
        as="textarea"
        ref={textareaRef}
        rows={1}
        value={layer.text ?? ''}
        disabled={Boolean(layer.textLocked)}
        onFocus={() => onFocusLayer(layer.id)}
        onChange={(event) => {
          const text = event.target.value;
          onUpdateRef.current(layer.id, (current) =>
            updateTextToFitLayer(current, text, maximumFontSizeRef.current)
          );
        }}
      />
      <div className="mt-1 d-flex justify-content-end">
        <Button
          type="button"
          size="sm"
          variant="link"
          className="px-0 fw-normal text-muted"
          onClick={() => onMoreOptions(layer.id)}
        >
          {moreOptionsLabel}
        </Button>
      </div>
    </Form.Group>
  );
};

const QuickTextLayerFields: React.FC<{
  layers: OverlayLayer[];
  language: OverlayEditorLanguage;
  onUpdate: (id: string, updater: (layer: OverlayLayer) => OverlayLayer) => void;
  onFocusLayer: (id: string) => void;
  onMoreOptions: (id: string) => void;
}> = ({ layers, language, onUpdate, onFocusLayer, onMoreOptions }) => {
  const { t: translate } = useTranslate();
  const t = useCallback(
    (key: string, defaultValue: string) =>
      translate({ key, defaultValue, language }),
    [language, translate]
  );

  return (
    <div className="d-grid gap-3 p-3">
      {layers.map((layer) => (
        <QuickTextLayerField
          key={layer.id}
          layer={layer}
          moreOptionsLabel={t(
            'overlayEditor.actions.moreOptions',
            'More options'
          )}
          onUpdate={onUpdate}
          onFocusLayer={onFocusLayer}
          onMoreOptions={onMoreOptions}
        />
      ))}
    </div>
  );
};

const LayerFields: React.FC<{
  layer: OverlayLayer;
  language: OverlayEditorLanguage;
  fontOptions: OverlayEditorFontOption[];
  showTextLock: boolean;
  allowTextDetailsWhenLayerLocked: boolean;
  canMoveBackward: boolean;
  canMoveForward: boolean;
  onFontSelect: (font: OverlayEditorFontOption) => void;
  onFontPickerFocus: () => void;
  onUpdate: (updater: (layer: OverlayLayer) => OverlayLayer) => void;
  onMove: (direction: -1 | 1) => void;
  onDelete: () => void;
  onFitToCanvas: () => void;
  onImageSelect: (file: File) => void | Promise<void>;
  onImageRemove: () => void;
}> = ({
  layer,
  language,
  fontOptions,
  showTextLock,
  allowTextDetailsWhenLayerLocked,
  canMoveBackward,
  canMoveForward,
  onFontSelect,
  onFontPickerFocus,
  onUpdate,
  onMove,
  onDelete,
  onFitToCanvas,
  onImageSelect,
  onImageRemove
}) => {
  const { t: translate } = useTranslate();
  const t = useCallback(
    (key: string, defaultValue: string) =>
      translate({ key, defaultValue, language }),
    [language, translate]
  );
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const textDetailsLocked =
    layer.kind === 'text' &&
    (layer.textLocked || (!allowTextDetailsWhenLayerLocked && layer.locked));
  const pintura: Record<string, unknown> =
    layer.kind === 'text'
      ? { text: layer.text, ...layer.typography }
      : layer.kind === 'shape'
      ? {
          backgroundColor: layer.fillColor,
          cornerRadius: layer.cornerRadius,
          shapeType: layer.shapeType ?? 'rectangle'
        }
      : layer.kind === 'background'
      ? {
          backgroundColor: layer.fillColor ?? '#ffffff',
          backgroundImage: layer.image?.src,
          backgroundSize: layer.image?.fit ?? 'cover'
        }
      : {
          backgroundImage: layer.image?.src,
          backgroundSize:
            layer.image?.fit === 'stretch'
              ? 'stretch'
              : layer.image?.fit === 'natural'
              ? 'natural'
              : layer.image?.fit ?? 'contain',
          cornerRadius: layer.cornerRadius,
          opacity: layer.image?.opacity ?? 1,
          borderColor: layer.image?.borderColor ?? '#000000',
          borderWidth: layer.image?.borderWidth ?? 0
        };
  const setPintura = (property: string, fieldValue: unknown) =>
    onUpdate((current) => {
      if (current.kind === 'text') {
        if (property === 'text') {
          return expandTextLayerToFit(current, String(fieldValue));
        }
        const typography = { ...current.typography };
        Object.assign(typography, { [property]: fieldValue });
        return { ...current, typography };
      }
      if (current.kind === 'background') {
        if (property === 'backgroundColor')
          return { ...current, fillColor: String(fieldValue) };
        const fit =
          fieldValue === 'contain'
            ? 'contain'
            : fieldValue === 'stretch'
            ? 'stretch'
            : 'cover';
        return {
          ...current,
          image: { ...current.image, src: current.image?.src, fit }
        };
      }
      if (current.kind === 'shape')
        return property === 'backgroundColor'
          ? { ...current, fillColor: String(fieldValue) }
          : property === 'shapeType'
          ? {
              ...current,
              shapeType: fieldValue as OverlayLayer['shapeType'],
              ...(fieldValue === 'straightLine'
                ? {
                    size: {
                      ...current.size,
                      height: Math.min(current.size.height, 12)
                    }
                  }
                : {})
            }
          : { ...current, cornerRadius: Number(fieldValue) };
      if (property === 'cornerRadius') {
        return { ...current, cornerRadius: Math.max(0, Number(fieldValue)) };
      }
      if (property === 'opacity') {
        return {
          ...current,
          image: {
            ...current.image,
            fit: current.image?.fit ?? 'contain',
            opacity: Math.min(1, Math.max(0, Number(fieldValue)))
          }
        };
      }
      if (property === 'borderColor' || property === 'borderWidth') {
        return {
          ...current,
          image: {
            ...current.image,
            fit: current.image?.fit ?? 'contain',
            [property]:
              property === 'borderWidth'
                ? Math.max(0, Number(fieldValue))
                : String(fieldValue)
          }
        };
      }
      const fit =
        fieldValue === 'contain'
          ? 'contain'
          : fieldValue === 'stretch'
          ? 'stretch'
          : fieldValue === 'natural'
          ? 'natural'
          : 'cover';
      return {
        ...current,
        image: { ...current.image, src: current.image?.src, fit }
      };
    });
  const setImageAspectRatioLock = (lockAspectRatio: boolean) =>
    onUpdate((current) => {
      if (current.kind !== 'background' && current.kind !== 'image')
        return current;
      return {
        ...current,
        image: {
          ...current.image,
          fit:
            current.image?.fit ??
            (current.kind === 'background' ? 'cover' : 'contain'),
          lockAspectRatio
        }
      };
    });
  const backgroundFillIsTransparent = layer.fillColor === 'transparent';
  const fitImageToCanvasButton = layer.image?.src ? (
    <Button
      variant="secondary"
      size="sm"
      className="rounded-3 d-inline-flex align-items-center justify-content-center gap-2"
      disabled={layer.locked}
      onClick={onFitToCanvas}
    >
      <i className="fa-duotone fa-arrows-maximize" aria-hidden="true" />
      {t('overlayEditor.image.fitToCanvas', 'Fit to canvas')}
    </Button>
  ) : null;
  const selectedFontFamily = String(pintura.fontFamily ?? 'Arial');
  // A new array here is a new `options` prop for the typeahead, which reruns
  // its own bookkeeping over the whole catalogue. It only ever changes when the
  // list or the chosen family does.
  const typeaheadFontOptions = useMemo(
    () =>
      fontOptions.some((font) => font.value === selectedFontFamily)
        ? fontOptions
        : [
            { value: selectedFontFamily, label: selectedFontFamily },
            ...fontOptions
          ],
    [fontOptions, selectedFontFamily]
  );
  return (
    <div>
      <div className="border-bottom p-3">
        <strong className="d-block text-truncate">{layer.name}</strong>
      </div>
      <div className="lomavis-template-layer-fields d-grid gap-2 p-3">
        <Form.Group>
          <Form.Label>{t('overlayEditor.layerName', 'Layer name')}</Form.Label>
          <Form.Control
            value={layer.name}
            disabled={layer.locked}
            onChange={(event) =>
              onUpdate((current) => ({
                ...current,
                name: event.target.value
              }))
            }
          />
        </Form.Group>
        {/* Everything you can do *to* the layer, rather than to one of its
            properties: kept together under the name instead of split between
            the panel header and the name field. Delete sits apart at the end,
            since it is the one that cannot be undone. */}
        <div className="d-flex align-items-center justify-content-end gap-1">
              <LomavisOverlayToolTip1
                placement="top"
                creatorStyle
                text={
                  layer.visible
                    ? t(
                        'overlayEditor.actions.hideLayerHelp',
                        'Hide this layer from the overlay.'
                      )
                    : t(
                        'overlayEditor.actions.showLayerHelp',
                        'Show this layer in the overlay.'
                      )
                }
              >
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="lomavis-template-layer-action lomavis-template-layer-name-action bg-white text-gray-700"
                  aria-label={t(
                    'overlayEditor.actions.toggleVisibility',
                    'Toggle layer visibility'
                  )}
                  onClick={() =>
                    onUpdate((current) => ({
                      ...current,
                      visible: !current.visible
                    }))
                  }
                >
                  <i
                    className={
                      layer.visible
                        ? overlayEditorIcons.eye
                        : overlayEditorIcons.eyeSlash
                    }
                    aria-hidden="true"
                  />
                </Button>
              </LomavisOverlayToolTip1>
              <LomavisOverlayToolTip1
                placement="top"
                creatorStyle
                text={
                  layer.locked
                    ? t(
                        'overlayEditor.actions.unlockLayerHelp',
                        'Unlock this layer to edit its properties.'
                      )
                    : t(
                        'overlayEditor.actions.lockLayerHelp',
                        'Lock this layer to prevent edits.'
                      )
                }
              >
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="lomavis-template-layer-action lomavis-template-layer-name-action bg-white text-gray-700"
                  aria-label={t(
                    'overlayEditor.actions.toggleLock',
                    'Toggle layer lock'
                  )}
                  onClick={() =>
                    onUpdate((current) => {
                      const locked = !current.locked;
                      return {
                        ...current,
                        locked,
                        ...(current.kind === 'text' && !locked
                          ? { textLocked: false }
                          : {})
                      };
                    })
                  }
                >
                  <i
                    className={
                      layer.locked
                        ? overlayEditorIcons.lock
                        : overlayEditorIcons.lockOpen
                    }
                    aria-hidden="true"
                  />
                </Button>
              </LomavisOverlayToolTip1>
          {layer.kind !== 'background' && (
            <>
              <LomavisOverlayToolTip1
                placement="bottom"
                creatorStyle
                text={t(
                  'overlayEditor.actions.sendBackHelp',
                  'Move this layer one position backward.'
                )}
              >
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="lomavis-template-layer-action bg-white text-gray-700"
                  aria-label={t(
                    'overlayEditor.actions.sendBack',
                    'Send back'
                  )}
                  disabled={!canMoveBackward}
                  onClick={() => onMove(-1)}
                >
                  <i
                    className={overlayEditorIcons.backward}
                    aria-hidden="true"
                  />
                </Button>
              </LomavisOverlayToolTip1>
              <LomavisOverlayToolTip1
                placement="bottom"
                creatorStyle
                text={t(
                  'overlayEditor.actions.bringForwardHelp',
                  'Move this layer one position forward.'
                )}
              >
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="lomavis-template-layer-action bg-white text-gray-700"
                  aria-label={t(
                    'overlayEditor.actions.bringForward',
                    'Bring forward'
                  )}
                  disabled={!canMoveForward}
                  onClick={() => onMove(1)}
                >
                  <i
                    className={overlayEditorIcons.forward}
                    aria-hidden="true"
                  />
                </Button>
              </LomavisOverlayToolTip1>
            </>
          )}
          <LomavisOverlayToolTip1
            placement="bottom"
            creatorStyle
            text={t(
              'overlayEditor.actions.deleteLayerHelp',
              'Permanently remove this layer from the overlay.'
            )}
          >
            <Button
              type="button"
              size="sm"
              variant="light"
              className="lomavis-template-layer-action ms-2 bg-white text-danger"
              aria-label={t(
                'overlayEditor.actions.deleteLayer',
                'Delete layer'
              )}
              disabled={layer.locked}
              onClick={() => setShowDeleteConfirmation(true)}
            >
              <i
                className={overlayEditorIcons.trash}
                aria-hidden="true"
              />
            </Button>
          </LomavisOverlayToolTip1>
        </div>
        {layer.kind === 'text' && (
          <>
            <div className="border-top pt-2 text-uppercase text-muted small fw-bold">
              {t('overlayEditor.typography', 'Typography')}
            </div>
            <Form.Group>
              <Form.Label>
                {t('overlayEditor.text.content', 'Text')}
              </Form.Label>
              <Form.Control
                as="textarea"
                value={String(pintura.text ?? '')}
                disabled={textDetailsLocked}
                onChange={(event) => setPintura('text', event.target.value)}
              />
            </Form.Group>
            {showTextLock && (
              <div className="d-flex align-items-center">
                <Form.Check
                  type="switch"
                  id={`template-editor-text-lock-${layer.id}`}
                  label={t('overlayEditor.text.lock', 'Lock text')}
                  checked={layer.textLocked ?? false}
                  onChange={(event) =>
                    onUpdate((current) => ({
                      ...current,
                      textLocked: event.target.checked,
                      locked: event.target.checked ? true : current.locked
                    }))
                  }
                />
                <LomavisMoreInfo
                  text={t(
                    'overlayEditor.text.lock.help',
                    'Prevents this text from being edited or moved.'
                  )}
                  iconClassName="fa-duotone fa-circle-info text-muted cursor-pointer ms-1"
                />
              </div>
            )}
            <div
              className="d-grid gap-2"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              <Form.Group>
                <Form.Label>{t('overlayEditor.text.font', 'Font')}</Form.Label>
                <LomavisFontTypeahead
                  id={`template-editor-font-${layer.id}`}
                  options={typeaheadFontOptions}
                  value={selectedFontFamily}
                  disabled={textDetailsLocked}
                  placeholder={t(
                    'overlayEditor.text.searchFont',
                    'Search fonts…'
                  )}
                  onFocus={onFontPickerFocus}
                  onPreview={loadBunnyFont}
                  onSelect={(font) => {
                    onFontSelect(font);
                    setPintura('fontFamily', font.value);
                  }}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>{t('overlayEditor.text.size', 'Size')}</Form.Label>
                <Form.Control
                  type="number"
                  value={Number(pintura.fontSize ?? 48)}
                  disabled={textDetailsLocked}
                  onChange={(event) =>
                    setPintura('fontSize', Number(event.target.value))
                  }
                />
              </Form.Group>
            </div>
            <Form.Group>
              <Form.Label>
                {t('overlayEditor.text.formatting', 'Formatting')}
              </Form.Label>
              <div className="lomavis-template-text-toolbar" role="toolbar">
                <Button
                  type="button"
                  size="sm"
                  variant={
                    pintura.fontWeight === 'bold' ? 'primary' : 'light'
                  }
                  className="lomavis-template-text-toolbar-button"
                  aria-label={t('overlayEditor.text.bold', 'Bold')}
                  aria-pressed={pintura.fontWeight === 'bold'}
                  disabled={textDetailsLocked}
                  onClick={() =>
                    setPintura(
                      'fontWeight',
                      pintura.fontWeight === 'bold' ? 'normal' : 'bold'
                    )
                  }
                >
                  <i
                    className={overlayEditorIcons.bold}
                    aria-hidden="true"
                  />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={
                    pintura.fontStyle === 'italic' ? 'primary' : 'light'
                  }
                  className="lomavis-template-text-toolbar-button"
                  aria-label={t('overlayEditor.text.italic', 'Italic')}
                  aria-pressed={pintura.fontStyle === 'italic'}
                  disabled={textDetailsLocked}
                  onClick={() =>
                    setPintura(
                      'fontStyle',
                      pintura.fontStyle === 'italic' ? 'normal' : 'italic'
                    )
                  }
                >
                  <i
                    className={overlayEditorIcons.italic}
                    aria-hidden="true"
                  />
                </Button>
                <span className="lomavis-template-text-toolbar-divider" />
                {[
                  {
                    value: 'left',
                    label: t('overlayEditor.text.left', 'Align left'),
                    icon: overlayEditorIcons.alignLeft
                  },
                  {
                    value: 'center',
                    label: t('overlayEditor.text.center', 'Align center'),
                    icon: overlayEditorIcons.alignCenter
                  },
                  {
                    value: 'right',
                    label: t('overlayEditor.text.right', 'Align right'),
                    icon: overlayEditorIcons.alignRight
                  }
                ].map(({ value, label, icon: Icon }) => (
                  <Button
                    key={value}
                    type="button"
                    size="sm"
                    variant={
                      (pintura.textAlign ?? 'left') === value
                        ? 'primary'
                        : 'light'
                    }
                    className="lomavis-template-text-toolbar-button"
                    aria-label={label}
                    aria-pressed={(pintura.textAlign ?? 'left') === value}
                    disabled={textDetailsLocked}
                    onClick={() => setPintura('textAlign', value)}
                  >
                    <i className={Icon} aria-hidden="true" />
                  </Button>
                ))}
                <span className="lomavis-template-text-toolbar-divider" />
                <LomavisSketchColorPicker
                  compact
                  value={String(pintura.color ?? '#000000')}
                  disabled={textDetailsLocked}
                  ariaLabel={t('overlayEditor.text.color', 'Text color')}
                  onChange={(value) => setPintura('color', value)}
                />
              </div>
            </Form.Group>
            <div
              className="d-grid gap-2"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              <Form.Group>
                <Form.Label>
                  {t('overlayEditor.text.lineHeight', 'Line height')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0.5"
                  step="0.05"
                  value={Number(pintura.lineHeight ?? 1.1)}
                  disabled={textDetailsLocked}
                  onChange={(event) =>
                    setPintura('lineHeight', Number(event.target.value))
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>
                  {t('overlayEditor.text.letterSpacing', 'Letter spacing')}
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={Number(pintura.letterSpacing ?? 0)}
                  disabled={textDetailsLocked}
                  onChange={(event) =>
                    setPintura('letterSpacing', Number(event.target.value))
                  }
                />
              </Form.Group>
            </div>
          </>
        )}
        {layer.kind === 'shape' && (
          <>
            <Form.Group>
              <Form.Label>{t('overlayEditor.shape.type', 'Shape')}</Form.Label>
              <BootstrapDropdown
                value={String(pintura.shapeType ?? 'rectangle')}
                disabled={layer.locked}
                ariaLabel={t('overlayEditor.shape.type', 'Shape')}
                onChange={(value) => setPintura('shapeType', value)}
                options={[
                  {
                    value: 'rectangle',
                    label: t('overlayEditor.shape.rectangle', 'Rectangle')
                  },
                  {
                    value: 'ellipse',
                    label: t('overlayEditor.shape.ellipse', 'Ellipse')
                  },
                  {
                    value: 'triangle',
                    label: t('overlayEditor.shape.triangle', 'Triangle')
                  },
                  {
                    value: 'straightLine',
                    label: t(
                      'overlayEditor.shape.straightLine',
                      'Straight line'
                    )
                  },
                  {
                    value: 'wavyLine',
                    label: t('overlayEditor.shape.wavyLine', 'Wavy line')
                  },
                  {
                    value: 'arrow',
                    label: t('overlayEditor.shape.arrow', 'Arrow')
                  },
                  {
                    value: 'diamond',
                    label: t('overlayEditor.shape.diamond', 'Diamond')
                  },
                  {
                    value: 'star',
                    label: t('overlayEditor.shape.star', 'Star')
                  }
                ]}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t('overlayEditor.shape.fill', 'Fill color')}
              </Form.Label>
              <LomavisSketchColorPicker
                value={String(pintura.backgroundColor ?? '#3359c7')}
                disabled={layer.locked}
                ariaLabel={t('overlayEditor.shape.fill', 'Fill color')}
                onChange={(value) => setPintura('backgroundColor', value)}
              />
            </Form.Group>
            {((layer.shapeType ?? 'rectangle') === 'rectangle' ||
              layer.shapeType === 'straightLine') && (
              <Form.Group>
                <Form.Label>
                  {t('overlayEditor.shape.cornerRadius', 'Corner radius')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={Number(pintura.cornerRadius ?? 0)}
                  disabled={layer.locked}
                  onChange={(event) =>
                    setPintura('cornerRadius', Number(event.target.value))
                  }
                />
              </Form.Group>
            )}
          </>
        )}
        {layer.kind === 'background' && (
          <>
            <div className="text-muted small">
              {t(
                'overlayEditor.background.hint',
                'The background stays behind every other layer. Select it on the canvas to move, resize, or rotate its image.'
              )}
            </div>
            <Form.Group>
              <Form.Label>
                {t('overlayEditor.background.fill', 'Fill color')}
              </Form.Label>
              <LomavisSketchColorPicker
                value={
                  backgroundFillIsTransparent
                    ? '#ffffff'
                    : String(pintura.backgroundColor ?? '#ffffff')
                }
                disabled={layer.locked}
                ariaLabel={t(
                  'overlayEditor.background.fill',
                  'Fill color'
                )}
                onChange={(value) => setPintura('backgroundColor', value)}
              />
            </Form.Group>
            {!backgroundFillIsTransparent && (
              <Button
                variant="outline-secondary"
                className="rounded-3"
                disabled={layer.locked}
                onClick={() => setPintura('backgroundColor', 'transparent')}
              >
                {t('overlayEditor.background.clearFill', 'Clear fill')}
              </Button>
            )}
            <LomavisImageUpload1
              disabled={layer.locked}
              onFileSelect={onImageSelect}
              imageSrc={layer.image?.src}
              onImageRemove={onImageRemove}
              removeLabel={t('overlayEditor.image.remove', 'Remove image')}
              acceptedFileTypes={TEMPLATE_IMAGE_ACCEPTED_TYPES}
              title={t('overlayEditor.imageUpload.title', 'Select an image to upload')}
              description={t(
                'overlayEditor.imageUpload.description',
                'or drag and drop a PNG or JPG image here'
              )}
              uploadingLabel={t(
                'overlayEditor.imageUpload.uploadingLabel',
                'Uploading image…'
              )}
              uploadingDescription={t(
                'overlayEditor.imageUpload.uploadingDescription',
                'Please wait while your image is uploaded.'
              )}
              invalidFileMessage={t(
                'overlayEditor.imageUpload.invalidFile',
                'Choose a PNG or JPG image.'
              )}
            />
            {layer.image?.src && (
              <Form.Check
                type="switch"
                id={`template-editor-lock-image-aspect-ratio-${layer.id}`}
                label={t(
                  'overlayEditor.image.lockAspectRatio',
                  'Lock image proportions'
                )}
                checked={layer.image.lockAspectRatio ?? false}
                disabled={layer.locked}
                onChange={(event) =>
                  setImageAspectRatioLock(event.target.checked)
                }
              />
            )}
            {fitImageToCanvasButton}
            {layer.image?.src && (
              <Form.Group>
                <Form.Label>
                  {t('overlayEditor.imageFit', 'Image fit')}
                </Form.Label>
                <BootstrapDropdown
                  value={String(pintura.backgroundSize ?? 'cover')}
                  disabled={layer.locked}
                  ariaLabel={t('overlayEditor.imageFit', 'Image fit')}
                  onChange={(value) => setPintura('backgroundSize', value)}
                  options={[
                    {
                      value: 'cover',
                      label: t('overlayEditor.fit.cover', 'Cover')
                    },
                    {
                      value: 'contain',
                      label: t('overlayEditor.fit.contain', 'Contain')
                    },
                    {
                      value: 'stretch',
                      label: t('overlayEditor.fit.stretch', 'Stretch to fill')
                    }
                  ]}
                />
              </Form.Group>
            )}
          </>
        )}
        {layer.kind === 'image' && (
          <>
            <LomavisImageUpload1
              disabled={layer.locked}
              onFileSelect={onImageSelect}
              imageSrc={layer.image?.src}
              onImageRemove={onImageRemove}
              removeLabel={t('overlayEditor.image.remove', 'Remove image')}
              acceptedFileTypes={TEMPLATE_IMAGE_ACCEPTED_TYPES}
              title={t('overlayEditor.imageUpload.title', 'Select an image to upload')}
              description={t(
                'overlayEditor.imageUpload.description',
                'or drag and drop a PNG or JPG image here'
              )}
              uploadingLabel={t(
                'overlayEditor.imageUpload.uploadingLabel',
                'Uploading image…'
              )}
              uploadingDescription={t(
                'overlayEditor.imageUpload.uploadingDescription',
                'Please wait while your image is uploaded.'
              )}
              invalidFileMessage={t(
                'overlayEditor.imageUpload.invalidFile',
                'Choose a PNG or JPG image.'
              )}
            />
            {layer.image?.src && (
              <Form.Check
                type="switch"
                id={`template-editor-lock-image-aspect-ratio-${layer.id}`}
                label={t(
                  'overlayEditor.image.lockAspectRatio',
                  'Lock image proportions'
                )}
                checked={layer.image.lockAspectRatio ?? true}
                disabled={layer.locked}
                onChange={(event) =>
                  setImageAspectRatioLock(event.target.checked)
                }
              />
            )}
            {fitImageToCanvasButton}
            <Form.Group>
              <Form.Label>
                {t('overlayEditor.imageFit', 'Image fit')}
              </Form.Label>
              <BootstrapDropdown
                value={
                  layer.image?.fit === 'natural'
                    ? 'contain'
                    : layer.image?.fit ?? 'cover'
                }
                disabled={layer.locked}
                ariaLabel={t('overlayEditor.imageFit', 'Image fit')}
                onChange={(value) => setPintura('backgroundSize', value)}
                options={[
                  {
                    value: 'contain',
                    label: t('overlayEditor.fit.contain', 'Contain')
                  },
                  {
                    value: 'cover',
                    label: t('overlayEditor.fit.cover', 'Cover')
                  },
                  {
                    value: 'stretch',
                    label: t('overlayEditor.fit.stretch', 'Stretch to fill')
                  }
                ]}
              />
            </Form.Group>
            <div
              className="d-grid gap-2"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              <Form.Group>
                <Form.Label>
                  {t('overlayEditor.image.cornerRadius', 'Corner radius')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={Number(pintura.cornerRadius ?? 0)}
                  disabled={layer.locked}
                  onChange={(event) =>
                    setPintura('cornerRadius', Number(event.target.value))
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>
                  {t('overlayEditor.image.opacity', 'Opacity')}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    value={Math.round(Number(pintura.opacity ?? 1) * 100)}
                    disabled={layer.locked}
                    onChange={(event) =>
                      setPintura('opacity', Number(event.target.value) / 100)
                    }
                  />
                  <InputGroup.Text
                    className={layer.locked ? 'text-muted' : ''}
                  >
                    %
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </div>
            <div
              className="d-grid gap-2"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              <Form.Group>
                <Form.Label>
                  {t('overlayEditor.image.borderColor', 'Border color')}
                </Form.Label>
                <LomavisSketchColorPicker
                  value={String(pintura.borderColor ?? '#000000')}
                  disabled={layer.locked}
                  ariaLabel={t(
                    'overlayEditor.image.borderColor',
                    'Border color'
                  )}
                  onChange={(value) => setPintura('borderColor', value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>
                  {t('overlayEditor.image.borderWidth', 'Border width')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={Number(pintura.borderWidth ?? 0)}
                  disabled={layer.locked}
                  onChange={(event) =>
                    setPintura('borderWidth', Number(event.target.value))
                  }
                />
              </Form.Group>
            </div>
          </>
        )}
      </div>
      <LomavisConfirmationModal
        show={showDeleteConfirmation}
        title={t('overlayEditor.deleteLayer.title', 'Delete layer?')}
        description={t(
          'overlayEditor.deleteLayer.description',
          'This layer will be permanently removed from the overlay.'
        )}
        cancelLabel={t('overlayEditor.actions.cancel', 'Cancel')}
        confirmLabel={t('overlayEditor.actions.delete', 'Delete')}
        buttonSize="sm"
        destructive
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={() => {
          setShowDeleteConfirmation(false);
          onDelete();
        }}
      />
    </div>
  );
};
